<template>
    <div class="video-detail-container" :class="{ 'theater-mode': theaterMode }" v-if="!isLoadingData">
        <template v-if="videoModel != null">
        <div class="video-main-content">
            <!-- Video Player Section -->
            <div class="video-player-section">
                <video-player
                    class="video-player"
                    v-show="!showBrokenMediaUpload"
                    :src="videoURL"
                    :poster="imageURL"
                    controls
                    :key="videoPlayerKey"
                    @play="togglePlaying"
                    :loop="false"
                    :volume="0.6"
                    @timeupdate="updateCurrentTime($event)"
                    @durationchange="updateTimeDuration($event)"
                    @ended="onVideoEnded"
                    @error="handleVideoLoadError"
                    @loadedmetadata="handleVideoLoaded"
                />

                <div v-if="showPlaybackError" class="video-playback-error">
                    <vue-feather type="alert-triangle" size="28" stroke-width="1.8"></vue-feather>
                    <h2>{{ currentLanguage === 'TA' ? 'காணொளி இயங்கவில்லை' : 'Video playback failed' }}</h2>
                    <p>
                        {{ currentLanguage === 'TA'
                            ? 'காணொளி கோப்பு உள்ளது, ஆனால் உலாவி அதை இப்போது ஏற்ற முடியவில்லை. பக்கத்தை மீண்டும் ஏற்றவும் அல்லது பிறகு முயற்சிக்கவும்.'
                            : 'The video file exists, but the browser could not load it right now. Refresh the page or try again shortly.' }}
                    </p>
                </div>

                <div v-if="showBrokenMediaUpload" class="broken-media-upload">
                    <div class="broken-media-upload-copy">
                        <div class="broken-media-upload-icon">
                            <vue-feather type="alert-triangle" size="22" stroke-width="2"></vue-feather>
                        </div>
                        <div>
                            <h2>{{ currentLanguage === 'TA' ? 'காணொளி கோப்பு இல்லை' : 'Video file missing' }}</h2>
                            <p>
                                {{ brokenMediaUploadCopy }}
                            </p>
                        </div>
                    </div>

                    <form class="missing-video-upload-form" @submit.prevent="submitBrokenMediaUpload">
                        <label class="missing-video-dropzone" :class="{ 'disabled': missingVideoUploading || missingVideoPreparing }">
                            <input
                                type="file"
                                accept="video/*,.mp4"
                                :disabled="missingVideoUploading || missingVideoPreparing"
                                @change="handleMissingVideoFileChange"
                            />
                            <vue-feather type="upload-cloud" size="28" stroke-width="1.8"></vue-feather>
                            <span>{{ missingVideoFile ? missingVideoFile.name : (currentLanguage === 'TA' ? 'மாற்று காணொளி கோப்பை தேர்ந்தெடுக்கவும்' : 'Choose replacement video file') }}</span>
                            <small>{{ currentLanguage === 'TA' ? 'MP4 அல்லது பொதுவான video கோப்புகள்' : 'MP4 or another standard video file' }}</small>
                        </label>

                        <div v-if="missingVideoUploading || missingVideoPreparing" class="missing-video-progress">
                            <div class="missing-video-progress-bar">
                                <div class="missing-video-progress-fill" :style="{ width: missingVideoUploadProgress + '%' }"></div>
                            </div>
                            <span>{{ missingVideoUploading ? Math.round(missingVideoUploadProgress) + '%' : (currentLanguage === 'TA' ? 'செயலாக்கம் நடக்கிறது' : 'Processing') }}</span>
                        </div>

                        <p v-if="missingVideoUploadMessage" :class="['missing-video-message', missingVideoUploadStatus]">
                            {{ missingVideoUploadMessage }}
                        </p>

                        <div class="missing-video-url-option">
                            <div class="missing-video-divider">
                                <span>{{ currentLanguage === 'TA' ? 'அல்லது' : 'or' }}</span>
                            </div>
                            <label class="missing-video-url-field">
                                <span>{{ currentLanguage === 'TA' ? 'காணொளி URL' : 'Video URL' }}</span>
                                <input
                                    type="url"
                                    v-model.trim="missingVideoSourceUrl"
                                    :placeholder="currentLanguage === 'TA' ? 'Dailymotion அல்லது ஆதரிக்கப்படும் காணொளி URL' : 'Dailymotion or supported video URL'"
                                    :disabled="missingVideoUploading || missingVideoPreparing"
                                />
                            </label>
                            <button
                                type="button"
                                class="submit-missing-video secondary"
                                :disabled="missingVideoUploading || missingVideoPreparing"
                                @click="submitBrokenMediaUrl"
                            >
                                <vue-feather v-if="missingVideoUploading || missingVideoPreparing" type="loader" animation="spin" size="16" stroke-width="2"></vue-feather>
                                <vue-feather v-else type="link" size="16" stroke-width="2"></vue-feather>
                                {{ currentLanguage === 'TA' ? 'URL-ஐ செயலாக்கு' : 'Process from URL' }}
                            </button>
                        </div>

                        <button
                            v-if="missingVideoFile || missingVideoUploading || missingVideoPreparing"
                            type="submit"
                            class="submit-missing-video"
                            :disabled="missingVideoUploading || missingVideoPreparing"
                        >
                            <vue-feather v-if="missingVideoUploading || missingVideoPreparing" type="loader" animation="spin" size="16" stroke-width="2"></vue-feather>
                            <vue-feather v-else type="upload" size="16" stroke-width="2"></vue-feather>
                            {{ missingVideoUploading
                                ? (currentLanguage === 'TA' ? 'பதிவேற்றுகிறது...' : 'Uploading...')
                                : missingVideoFile
                                    ? (currentLanguage === 'TA' ? 'மாற்று காணொளியை பதிவேற்று' : 'Upload replacement video')
                                    : (currentLanguage === 'TA' ? 'முதலில் கோப்பை தேர்ந்தெடுக்கவும்' : 'Choose a file first') }}
                        </button>
                    </form>
                </div>

                <!-- Auto-play Next Overlay -->
                <div class="autoplay-overlay" v-if="showAutoplayOverlay && nextVideo && !showBrokenMediaUpload">
                    <div class="autoplay-info">
                        <div class="autoplay-thumbnail" :style="`background-image: url('${imageURLForVideo(nextVideo.id)}')`"></div>
                        <div class="autoplay-details">
                            <span class="autoplay-label">Up Next</span>
                            <span class="autoplay-title">{{ decodeHtml(currentLanguage === 'EN' ? (nextVideo.titleEN || nextVideo.titleTA) : (nextVideo.titleTA || nextVideo.titleEN)) }}</span>
                        </div>
                    </div>
                    <div class="autoplay-countdown">Playing in {{ autoplayCountdown }}...</div>
                    <div class="autoplay-progress-bar">
                        <div class="autoplay-progress-fill" :style="{ width: ((5 - autoplayCountdown) / 5 * 100) + '%' }"></div>
                    </div>
                    <div class="autoplay-actions">
                        <button class="autoplay-play-now" @click="playNextNow">{{ currentLanguage === 'TA' ? 'இப்போது இயக்கு' : 'Play Now' }}</button>
                        <button class="autoplay-cancel" @click="cancelAutoplay">{{ currentLanguage === 'TA' ? 'ரத்து' : 'Cancel' }}</button>
                    </div>
                </div>
            </div>

            <!-- Video Info Section -->
            <div class="video-info-section">
                <div class="video-header">
                    <div class="video-title-section">
                        <h1 class="video-title">{{ decodeHtml(currentLanguage === 'EN' ? (videoModel.titleEN || videoModel.titleTA) : (videoModel.titleTA || videoModel.titleEN)) }}</h1>
                        <div class="video-meta">
                            <span class="views-count">
                                <vue-feather type="play" size="14" stroke-width="2" class="meta-icon"></vue-feather>
                                {{ formatNumber(videoModel.viewsCount) }} {{ currentLanguage === 'TA' ? tamilPlural(videoModel.viewsCount || 0, 'பார்வை', 'பார்வைகள்') : 'plays' }}
                            </span>
                            <span class="dot-separator">•</span>
                            <span class="upload-date">
                                <vue-feather type="clock" size="14" stroke-width="2" class="meta-icon"></vue-feather>
                                {{ timeAgo(Date.parse(videoModel.createdAt)) }}
                            </span>
                            <span class="dot-separator">•</span>
                            <span class="video-duration">
                                <vue-feather type="clock" size="14" stroke-width="2" class="meta-icon"></vue-feather>
                                {{ formattedDuration(videoModel.duration) }}
                            </span>
                            <span v-if="videoModel.significantDate" class="dot-separator">•</span>
                            <span v-if="videoModel.significantDate" class="significant-date">
                                <vue-feather type="calendar" size="14" stroke-width="2" class="meta-icon"></vue-feather>
                                {{ formatSignificantDate(videoModel.significantDate) }}
                            </span>
                        </div>
                        <div class="video-owner-status" v-if="canEditCurrentVideo">
                            <span class="video-status-pill" :class="videoStatusClass">{{ videoStatusLabel }}</span>
                            <span class="video-status-pill" :class="videoPublishStateClass">{{ videoPublishStateLabel }}</span>
                        </div>
                    </div>
                    <div class="video-actions" v-if="canEditCurrentVideo">
                        <router-link class="edit-button" :to="`/edit/${videoModel.id}`">
                            <vue-feather type="edit-3" size="16"></vue-feather>
                            {{ currentLanguage === 'TA' ? 'திருத்து' : 'Edit' }}
                        </router-link>
                    </div>
                </div>

                <!-- Interaction Section -->
                <div class="interaction-section">
                    <div class="interaction-bar" :class="{ 'interaction-loading': liked === null && userId }">
                        <div class="interaction-left">
                            <button
                                class="action-button like-button"
                                :class="{ 'active': liked }"
                                :disabled="loading || !userId || liked === null"
                                @click="liked ? unLikeVideo() : likeVideo()"
                            >
                                <vue-feather :type="liked ? 'thumbs-up' : 'thumbs-up'" size="18" :fill="liked ? 'currentColor' : 'none'" stroke-width="2"></vue-feather>
                                <span>{{ formatNumber(videoModel.likesCount ?? 0) }}</span>
                            </button>
                            <button
                                class="action-button dislike-button"
                                :class="{ 'active': disliked }"
                                :disabled="loading || !userId || disliked === null"
                                @click="disliked ? unDislikeVideo() : dislikeVideo()"
                            >
                                <vue-feather :type="disliked ? 'thumbs-down' : 'thumbs-down'" size="18" :fill="disliked ? 'currentColor' : 'none'" stroke-width="2"></vue-feather>
                                <span>{{ formatNumber(videoModel.dislikesCount ?? 0) }}</span>
                            </button>
                        </div>
                        <div class="interaction-center">
                            <button
                                class="action-button share-button"
                                @click="shareVideo"
                            >
                                <vue-feather type="share-2" size="18" stroke-width="2"></vue-feather>
                                <span>{{ shareCopied ? (currentLanguage === 'TA' ? 'நகலெடுக்கப்பட்டது!' : 'Copied!') : (currentLanguage === 'TA' ? 'பகிர' : 'Share') }}</span>
                            </button>
                            <a
                                v-if="showDownloadLink"
                                class="action-button download-button"
                                :href="downloadURL"
                                download
                                target="_blank"
                                @click="trackDownload"
                            >
                                <vue-feather type="download" size="18" stroke-width="2"></vue-feather>
                                <span>{{ currentLanguage === 'TA' ? 'பதிவிறக்கம்' : 'Download' }}</span>
                            </a>
                        </div>
                        <div class="interaction-right">
                            <button
                                class="action-button theater-mode-button"
                                @click="toggleTheaterMode"
                                :title="theaterMode ? 'Default view' : 'Theater mode'"
                            >
                                <vue-feather :type="theaterMode ? 'minimize' : 'maximize'" size="18" stroke-width="2"></vue-feather>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Channel Info Section -->
            <div class="channel-section" v-if="videoModel.owner != null">
                <div class="channel-info">
                    <div class="channel-avatar">
                        <router-link :to="`/@${videoModel.owner.username}`">
                            <img :src="getOwnerAvatar(videoModel.owner)" alt="Channel Avatar" @error="handleAvatarError" />
                        </router-link>
                    </div>
                    <div class="channel-details">
                        <div class="channel-name-section">
                            <router-link class="channel-name" :to="`/@${videoModel.owner.username}`">
                                {{ displayName(videoModel.owner) }}
                            </router-link>
                            <vue-feather v-if="videoModel.owner.status === 'verified'" type="check-circle" class="verified-badge" size="16" stroke-width="2"></vue-feather>
                        </div>
                        <div class="channel-stats">
                            <span class="subscriber-count">{{ formatNumber(subscribersCount) }} subscribers</span>
                        </div>
                    </div>
                    <div class="channel-actions" v-if="userId && userId != videoModel.owner.id">
                        <button
                            class="subscribe-button"
                            :class="{ 'subscribed': subscription != null }"
                            :disabled="loading"
                            @click="subscribeToggle"
                        >
                            <vue-feather v-if="subscription != null" type="check" size="16" stroke-width="2"></vue-feather>
                            <span>{{ subscription != null ? 'Subscribed' : 'Subscribe' }}</span>
                        </button>
                        <button
                            class="notification-button"
                            :class="{ 'notifications-enabled': notificationsEnabled }"
                            :disabled="loading || !subscription"
                            @click="toggleNotifications"
                            :title="!subscription ? 'Subscribe first to enable notifications' : (notificationsEnabled ? 'Notifications enabled' : 'Enable notifications')"
                        >
                            <vue-feather
                                :type="notificationsEnabled ? 'bell' : 'bell-off'"
                                size="18"
                                :fill="notificationsEnabled ? 'currentColor' : 'none'"
                                stroke-width="2"
                            ></vue-feather>
                        </button>
                        <router-link
                            class="message-button"
                            :to="`/messages/${videoModel.owner.id}`"
                            title="Send a message"
                        >
                            <vue-feather type="mail" size="18" stroke-width="2"></vue-feather>
                        </router-link>
                    </div>
                </div>
                <div class="video-description">
                    <p 
                        :class="{ 'description-collapsed': !showFullDescription }"
                        ref="descriptionText"
                        v-html="formatDescription(currentLanguage === 'EN' ? (videoModel.descriptionEN || videoModel.descriptionTA) : (videoModel.descriptionTA || videoModel.descriptionEN))"
                    ></p>
                    <button 
                        v-if="descriptionNeedsTruncation" 
                        @click="toggleDescription" 
                        class="show-more-button"
                    >
                        <vue-feather 
                            :type="showFullDescription ? 'chevron-up' : 'chevron-down'" 
                            size="16" 
                            stroke-width="2"
                        ></vue-feather>
                        {{ showFullDescription ? (currentLanguage === 'TA' ? 'குறைவாகக் காட்டு' : 'Show less') : (currentLanguage === 'TA' ? 'மேலும் காட்டு' : 'Show more') }}
                    </button>
                    <div v-if="videoModel.significantDate" class="significant-date-section">
                        <h4 class="significant-date-title">
                            <vue-feather type="calendar" size="16" stroke-width="2"></vue-feather>
                            {{ currentLanguage === 'TA' ? 'முக்கிய தேதி' : 'Significant Date' }}
                        </h4>
                        <p class="significant-date-value">{{ formatSignificantDate(videoModel.significantDate) }}</p>
                    </div>
                </div>
                
                <!-- Categories Section -->
                <div class="video-categories" v-if="videoModel.primaryCategory || videoModel.secondaryCategory">
                    <h4 class="categories-title">{{ currentLanguage === 'TA' ? 'வகைகள்:' : 'Categories:' }}</h4>
                    <div class="categories-list">
                        <router-link 
                            v-if="videoModel.primaryCategory"
                            :to="`/category/${videoModel.primaryCategory.id}`"
                            class="category-link primary-category"
                        >
                            <vue-feather type="folder" size="14" stroke-width="2"></vue-feather>
                            {{ currentLanguage === 'EN' ? videoModel.primaryCategory.nameEN : videoModel.primaryCategory.nameTA }}
                            <span class="category-badge">{{ currentLanguage === 'TA' ? 'முதன்மை' : 'Primary' }}</span>
                        </router-link>
                        <router-link 
                            v-if="videoModel.secondaryCategory"
                            :to="`/category/${videoModel.secondaryCategory.id}`"
                            class="category-link secondary-category"
                        >
                            <vue-feather type="folder" size="14" stroke-width="2"></vue-feather>
                            {{ currentLanguage === 'EN' ? videoModel.secondaryCategory.nameEN : videoModel.secondaryCategory.nameTA }}
                            <span class="category-badge">{{ currentLanguage === 'TA' ? 'இரண்டாம்' : 'Secondary' }}</span>
                        </router-link>
                    </div>
                </div>

                <!-- Tags Section -->
                <div class="video-tags" v-if="videoModel.tags && videoModel.tags.items && videoModel.tags.items.length > 0">
                    <h4 class="tags-title">{{ currentLanguage === 'TA' ? 'குறிச்சொற்கள்:' : 'Tags:' }}</h4>
                    <div class="tags-list">
                        <router-link 
                            v-for="tagItem in videoModel.tags.items" 
                            :key="tagItem.id"
                            :to="`/tag/${tagItem.tagModel.label}`"
                            class="tag-link"
                        >
                            #{{ tagItem.tagModel.label }}
                        </router-link>
                    </div>
                </div>
            </div>

            <!-- Comments Section -->
            <div class="comments-section">
                <div class="comments-header">
                    <h3 class="comments-title">{{ videoModel.commentsCount || 0 }} {{ currentLanguage === 'TA' ? 'கருத்துகள்' : 'Comments' }}</h3>
                </div>
                
                <div class="comment-form" v-if="userId">
                    <div class="comment-input-container">
                        <div class="user-avatar-small">
                            <img :src="currentUserAvatar" alt="Your Avatar" @error="handleAvatarError" />
                        </div>
                        <div class="comment-input-wrapper">
                            <textarea
                                v-model="newComment"
                                class="comment-input"
                                :placeholder="currentLanguage === 'TA' ? 'கருத்தைச் சேர்க்கவும்...' : 'Add a comment...'"
                                rows="1"
                                @input="autoResize"
                            ></textarea>
                            <div class="comment-actions">
                                <button
                                    class="post-comment-button"
                                    :disabled="!newComment || loading"
                                    @click="postComment"
                                >
                                    {{ currentLanguage === 'TA' ? 'பதிவிடு' : 'Post' }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="comment-login-prompt" v-else>
                    <a href="#" class="login-to-comment" @click.prevent="openLogin">{{ currentLanguage === 'TA' ? 'கருத்திட உள்நுழையவும்' : 'Log in to comment' }}</a>
                </div>

                <div class="comments-list" v-if="sortedComments.length">
                    <div class="comment-item-wrapper" v-for="comment in sortedComments" :key="comment.id">
                        <div class="comment-item">
                            <div class="comment-avatar">
                                <router-link :to="comment.owner?.username ? `/@${comment.owner.username}` : '#'">
                                    <img :src="getOwnerAvatar(comment.owner)" alt="User Avatar" @error="handleAvatarError" />
                                </router-link>
                            </div>
                            <div class="comment-content">
                                <div class="comment-header">
                                    <router-link class="comment-author" :to="comment.owner?.username ? `/@${comment.owner.username}` : '#'">
                                        {{ comment.owner ? displayName(comment.owner) : 'Unknown User' }}
                                        <vue-feather v-if="comment.owner?.status === 'verified'" type="check-circle" class="verified-badge-small" size="12" stroke-width="2"></vue-feather>
                                    </router-link>
                                    <span class="comment-timestamp">{{ timeAgo(Date.parse(comment.createdAt)) }}</span>
                                    <button
                                        v-if="isOwner(comment)"
                                        class="delete-comment-button"
                                        :disabled="loading"
                                        @click="deleteComment(comment)"
                                    >
                                        <vue-feather type="trash-2" size="14" stroke-width="2"></vue-feather>
                                    </button>
                                </div>
                                <div class="comment-text" v-html="formatUserContent(comment.message)">
                                </div>
                                <div class="comment-actions-row">
                                    <button @click="likeComment(comment)" :disabled="!userId" :class="{ 'active': commentInteractions[comment.id]?.liked }">
                                        <vue-feather type="thumbs-up" size="14" :fill="commentInteractions[comment.id]?.liked ? 'currentColor' : 'none'" stroke-width="2"></vue-feather>
                                        <span>{{ comment.likesCount || 0 }}</span>
                                    </button>
                                    <button @click="dislikeComment(comment)" :disabled="!userId" :class="{ 'active': commentInteractions[comment.id]?.disliked }">
                                        <vue-feather type="thumbs-down" size="14" :fill="commentInteractions[comment.id]?.disliked ? 'currentColor' : 'none'" stroke-width="2"></vue-feather>
                                        <span>{{ comment.dislikesCount || 0 }}</span>
                                    </button>
                                    <button @click="startReply(comment)" v-if="userId" class="reply-btn">
                                        <vue-feather type="corner-up-left" size="14" stroke-width="2"></vue-feather>
                                        {{ currentLanguage === 'TA' ? 'பதில்' : 'Reply' }}
                                    </button>
                                </div>

                                <!-- Inline reply form -->
                                <div class="reply-form" v-if="replyingTo === comment.id">
                                    <textarea
                                        :ref="'replyInput-' + comment.id"
                                        v-model="replyText"
                                        class="reply-input"
                                        :placeholder="currentLanguage === 'TA' ? 'பதிலை எழுதுங்கள்...' : 'Write a reply...'"
                                        rows="2"
                                    ></textarea>
                                    <div class="reply-form-actions">
                                        <button class="reply-cancel-btn" @click="cancelReply">{{ currentLanguage === 'TA' ? 'ரத்து' : 'Cancel' }}</button>
                                        <button class="reply-post-btn" :disabled="!replyText.trim() || loading" @click="postReply(comment)">{{ currentLanguage === 'TA' ? 'பதில்' : 'Reply' }}</button>
                                    </div>
                                </div>

                                <!-- Toggle replies -->
                                <button
                                    v-if="repliesCount(comment) > 0"
                                    class="toggle-replies-btn"
                                    @click="toggleReplies(comment.id)"
                                >
                                    <vue-feather :type="expandedReplies[comment.id] ? 'chevron-up' : 'chevron-down'" size="14" stroke-width="2"></vue-feather>
                                    {{ expandedReplies[comment.id] ? (currentLanguage === 'TA' ? 'மறை' : 'Hide') : (currentLanguage === 'TA' ? 'காண்' : 'View') }} {{ repliesCount(comment) }} {{ currentLanguage === 'TA' ? 'பதில்கள்' : (repliesCount(comment) === 1 ? 'reply' : 'replies') }}
                                </button>
                            </div>
                        </div>

                        <!-- Nested replies -->
                        <div class="replies-container" v-if="expandedReplies[comment.id] && repliesCount(comment) > 0">
                            <div class="reply-item" v-for="reply in comment.replies.items" :key="reply.id">
                                <div class="reply-avatar">
                                    <router-link :to="reply.owner?.username ? `/@${reply.owner.username}` : '#'">
                                        <img :src="getOwnerAvatar(reply.owner)" alt="User Avatar" @error="handleAvatarError" />
                                    </router-link>
                                </div>
                                <div class="comment-content">
                                    <div class="comment-header">
                                        <router-link class="comment-author" :to="reply.owner?.username ? `/@${reply.owner.username}` : '#'">
                                            {{ reply.owner ? displayName(reply.owner) : 'Unknown User' }}
                                            <vue-feather v-if="reply.owner?.status === 'verified'" type="check-circle" class="verified-badge-small" size="12" stroke-width="2"></vue-feather>
                                        </router-link>
                                        <span class="comment-timestamp">{{ timeAgo(Date.parse(reply.createdAt)) }}</span>
                                        <button
                                            v-if="isOwner(reply)"
                                            class="delete-comment-button"
                                            :disabled="loading"
                                            @click="deleteReply(reply, comment)"
                                        >
                                            <vue-feather type="trash-2" size="14" stroke-width="2"></vue-feather>
                                        </button>
                                    </div>
                                    <div class="comment-text" v-html="formatUserContent(reply.message)">
                                    </div>
                                    <div class="comment-actions-row">
                                        <button @click="likeComment(reply)" :disabled="!userId" :class="{ 'active': commentInteractions[reply.id]?.liked }">
                                            <vue-feather type="thumbs-up" size="14" :fill="commentInteractions[reply.id]?.liked ? 'currentColor' : 'none'" stroke-width="2"></vue-feather>
                                            <span>{{ reply.likesCount || 0 }}</span>
                                        </button>
                                        <button @click="dislikeComment(reply)" :disabled="!userId" :class="{ 'active': commentInteractions[reply.id]?.disliked }">
                                            <vue-feather type="thumbs-down" size="14" :fill="commentInteractions[reply.id]?.disliked ? 'currentColor' : 'none'" stroke-width="2"></vue-feather>
                                            <span>{{ reply.dislikesCount || 0 }}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <VideoRecommendations
                :current-user="currentUser"
                :current-video="videoModel"
                :title="currentLanguage === 'TA' ? 'பரிந்துரைக்கப்பட்டவை ' : 'Recommended Videos'"
            />
        </div>

        <!-- Related Videos Sidebar -->
        <div class="related-videos-sidebar">
            <div class="sidebar-header">
                <h3 class="sidebar-title">{{ currentLanguage === 'TA' ? 'தொடர்புடையவை  ' : 'Related Videos' }}</h3>
                <div class="autoplay-toggle">
                    <span class="autoplay-toggle-label">{{ currentLanguage === 'TA' ? 'தானியக்கம்' : 'Autoplay' }}</span>
                    <label class="toggle-switch">
                        <input type="checkbox" :checked="autoplayEnabled" @change="toggleAutoplay">
                        <span class="toggle-slider"></span>
                    </label>
                </div>
            </div>

            <div v-if="!isLoadingSidebarVideos" class="related-videos-list">
                <div v-for="(video, index) in allSidebarVideos" :key="video.id" class="related-video-item">
                    <router-link :to="`/watch/${video.id}`" class="video-thumbnail-link">
                        <div class="video-thumbnail">
                            <img
                                :src="imageURLForVideo(video.id)"
                                :alt="video.titleEN || video.titleTA"
                                @error="handleThumbnailError"
                            />
                            <div class="thumbnail-duration">{{ formattedDuration(video.duration) }}</div>
                            <div v-if="index === 0" class="up-next-badge">{{ currentLanguage === 'TA' ? 'அடுத்தது' : 'UP NEXT' }}</div>
                        </div>
                    </router-link>

                    <div class="video-info-compact">
                        <router-link class="video-title-compact" :to="`/watch/${video.id}`">
                            {{ decodeHtml(currentLanguage === 'EN' ? (video.titleEN || video.titleTA) : (video.titleTA || video.titleEN)) }}
                        </router-link>
                        <div class="video-meta-compact">
                            <router-link class="channel-name-compact" :to="`/@${getVideoOwner(video).username}`">
                                {{ displayName(getVideoOwner(video)) }}
                            </router-link>
                            <vue-feather v-if="getVideoOwner(video).status === 'verified'" type="check-circle" class="verified-badge-small" size="12" stroke-width="2"></vue-feather>
                        </div>
                        <div class="video-stats-compact">
                            {{ formatNumber(video.viewsCount || 0) }} {{ currentLanguage === 'TA' ? tamilPlural(video.viewsCount || 0, 'பார்வை', 'பார்வைகள்') : 'views' }} • {{ timeAgo(Date.parse(video.createdAt)) }}
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!isLoadingSidebarVideos && allSidebarVideos.length == 0" class="no-related-videos">
                <vue-feather type="video-off" size="24" stroke-width="2"></vue-feather>
                <p>{{ currentLanguage === 'TA' ? 'தொடர்புடையவை   இல்லை' : 'No related videos found' }}</p>
            </div>
            
            <div v-if="isLoadingSidebarVideos" class="shimmer-sidebar-list">
                <div class="shimmer-sidebar-item" v-for="n in 6" :key="'shimmer-rel-' + n">
                    <div class="shimmer-sidebar-thumb"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-sidebar-info">
                        <div class="shimmer-sidebar-line full"><div class="shimmer-overlay"></div></div>
                        <div class="shimmer-sidebar-line medium"><div class="shimmer-overlay"></div></div>
                        <div class="shimmer-sidebar-line short"><div class="shimmer-overlay"></div></div>
                    </div>
                </div>
            </div>
        </div>
        </template>

        <api-error-state v-else-if="videoLoadError" source="video_detail" @retry="fetchVideoDetails" />

        <div v-else-if="videoNotFound" class="video-not-found">
            <div class="video-not-found-icon">
                <vue-feather type="video-off" size="42" stroke-width="1.8"></vue-feather>
            </div>
            <h1>{{ currentLanguage === 'TA' ? 'காணொளி கிடைக்கவில்லை' : 'Video not found' }}</h1>
            <p>
                {{ currentLanguage === 'TA'
                    ? 'இந்த காணொளி நீக்கப்பட்டிருக்கலாம் அல்லது இணைப்பு தவறாக இருக்கலாம்.'
                    : 'This video may have been removed, or the link may be incorrect.' }}
            </p>
            <div class="video-not-found-actions">
                <router-link class="primary-not-found-action" to="/">
                    <vue-feather type="home" size="16" stroke-width="2"></vue-feather>
                    {{ currentLanguage === 'TA' ? 'முகப்புக்கு செல்' : 'Go home' }}
                </router-link>
                <router-link class="secondary-not-found-action" to="/videos">
                    {{ currentLanguage === 'TA' ? 'காணொளிகளை பார்க்க' : 'Browse videos' }}
                </router-link>
            </div>

        </div>
    </div>

    <!-- Loading Shimmer State -->
    <div class="video-detail-container shimmer-container" v-if="isLoadingData">
        <div class="video-main-content">
            <!-- Player shimmer -->
            <div class="shimmer-player">
                <div class="shimmer-overlay"></div>
            </div>

            <!-- Video info shimmer -->
            <div class="shimmer-info-section">
                <div class="shimmer-title-long"><div class="shimmer-overlay"></div></div>
                <div class="shimmer-title-medium"><div class="shimmer-overlay"></div></div>
                <div class="shimmer-meta-row">
                    <div class="shimmer-meta-chip"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-meta-chip"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-meta-chip"><div class="shimmer-overlay"></div></div>
                </div>
                <div class="shimmer-actions-row">
                    <div class="shimmer-action-btn"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-action-btn"><div class="shimmer-overlay"></div></div>
                </div>
            </div>

            <!-- Channel shimmer -->
            <div class="shimmer-channel-section">
                <div class="shimmer-channel-row">
                    <div class="shimmer-channel-avatar"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-channel-text">
                        <div class="shimmer-channel-name"><div class="shimmer-overlay"></div></div>
                        <div class="shimmer-channel-subs"><div class="shimmer-overlay"></div></div>
                    </div>
                    <div class="shimmer-subscribe-btn"><div class="shimmer-overlay"></div></div>
                </div>
                <div class="shimmer-desc-line full"><div class="shimmer-overlay"></div></div>
                <div class="shimmer-desc-line full"><div class="shimmer-overlay"></div></div>
                <div class="shimmer-desc-line partial"><div class="shimmer-overlay"></div></div>
            </div>

            <!-- Comments shimmer -->
            <div class="shimmer-comments-section">
                <div class="shimmer-comments-header"><div class="shimmer-overlay"></div></div>
                <div class="shimmer-comment" v-for="n in 3" :key="'shimmer-comment-' + n">
                    <div class="shimmer-comment-avatar"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-comment-body">
                        <div class="shimmer-comment-name"><div class="shimmer-overlay"></div></div>
                        <div class="shimmer-comment-text"><div class="shimmer-overlay"></div></div>
                        <div class="shimmer-comment-text short"><div class="shimmer-overlay"></div></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sidebar shimmer -->
        <div class="related-videos-sidebar">
            <div class="shimmer-sidebar-title"><div class="shimmer-overlay"></div></div>
            <div class="shimmer-sidebar-item" v-for="n in 6" :key="'shimmer-sidebar-' + n">
                <div class="shimmer-sidebar-thumb"><div class="shimmer-overlay"></div></div>
                <div class="shimmer-sidebar-info">
                    <div class="shimmer-sidebar-line full"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-sidebar-line medium"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-sidebar-line short"><div class="shimmer-overlay"></div></div>
                </div>
            </div>
        </div>
    </div>
</template>
  
<script>
import { VideoPlayer } from '@videojs-player/vue'
import ApiErrorState from './ApiErrorState.vue'
// import 'video.js/dist/video-js.css'

import { API } from 'aws-amplify'
import { Storage } from '@aws-amplify/storage'
import { track as analyticsTrack, captureError } from '../utils/analytics'
import { canEditVideo } from '../utils/auth.js'
import { getVideoModelWithLikesAndDislikes, videosByStatusWithOwner, getUserModelByLegacyId, getUserModelLiteByUsername, checkSubscription, createInteractionSimple, deleteInteractionSimple, createCommentInteractionSimple, deleteCommentInteractionSimple, commentInteractionsByUserAndVideo, getRecommendationsQuery, getVideoProcessingStatus } from '../graphql/custom'
import {
    createCommentModel,
    createUserSubscriber,
    updateUserSubscriber,
    deleteCommentModel,
    deleteUserSubscriber,
} from '../graphql/mutations'

export default {
    name: 'VideoDetail',
    components: {
        VideoPlayer,
        ApiErrorState
    },
    inject: {
        languageRef: { from: 'currentLanguage', default: 'EN' },
        openLogin: { default: () => () => {} }
    },
    props: {
        // For legacy URLs like /watch/title-slug_videoId.html
        legacySlug: {
            type: String,
            default: null
        }
    },
    async created() {
        console.log('created', this.currentUser)

        try {
            let user = await API.Auth.currentAuthenticatedUser()
            console.log('Current User Info', user)
            this.currentUser = user.attributes

            // Try to get UserModel ID from parent first
            if (this.$parent?.currentUser?.model?.id) {
                this.userId = this.$parent.currentUser.model.id;
                this.currentUserModel = this.$parent.currentUser.model;
                console.log('VideoDetail - using userId from parent:', this.userId);
            } else {
                // Fetch user model by legacyId if we have one
                const legacyUserId = this.currentUser['custom:legacyUserId'];
                if (legacyUserId) {
                    try {
                        const res = await API.graphql({
                            query: getUserModelByLegacyId,
                            variables: { legacyId: legacyUserId }
                        });
                        if (res.data.userByLegacyId?.items?.length > 0) {
                            this.currentUserModel = res.data.userByLegacyId.items[0];
                            this.userId = this.currentUserModel.id;
                            console.log('VideoDetail - fetched user by legacyId:', this.userId);
                        } else {
                            // Fallback to Cognito sub
                            this.userId = this.currentUser.sub;
                            console.log('VideoDetail - user not found, using Cognito sub:', this.userId);
                        }
                    } catch (error) {
                        console.error('VideoDetail - error fetching user:', error);
                        this.userId = this.currentUser.sub;
                    }
                } else if (user.username) {
                    // No legacyUserId - try to find DynamoDB model by Cognito username
                    try {
                        const res = await API.graphql({
                            query: getUserModelLiteByUsername,
                            variables: { username: user.username }
                        });
                        if (res.data.userByUsername?.items?.length > 0) {
                            this.currentUserModel = res.data.userByUsername.items[0];
                            this.userId = this.currentUserModel.id;
                            console.log('VideoDetail - fetched user by username:', this.userId);
                        } else {
                            this.userId = this.currentUser.sub;
                            console.log('VideoDetail - user not found by username, using Cognito sub:', this.userId);
                        }
                    } catch (error) {
                        this.userId = this.currentUser.sub;
                    }
                } else {
                    this.userId = this.currentUser.sub;
                    console.log('VideoDetail - no legacyUserId or username, using Cognito sub:', this.userId);
                }
            }
        } catch (e) {
            console.log('Guest user - not authenticated');
            this.userId = null;
            this.currentUser = null;
        }

        this.reloadData();
    },
    watch: {
        'videoModel.descriptionTA': {
            handler() {
                this.$nextTick(() => {
                    this.checkDescriptionTruncation();
                });
            }
        },
        '$parent.currentUser.model': {
            handler(newModel) {
                if (newModel && newModel.id) {
                    const oldUserId = this.userId;
                    this.currentUserModel = newModel;
                    this.userId = newModel.id;
                    console.log('VideoDetail - updated userId from parent model:', this.userId);
                    // If userId changed and video is loaded, re-check interaction status
                    if (oldUserId !== this.userId && this.videoModel) {
                        this.checkEditAuthorization();
                        this.fetchUserInteraction();
                        this.fetchCommentInteractions();
                    }
                }
            },
            immediate: true
        }
    },
    mounted(){
        this.$watch( ()=> this.$route.path,(to, from)=> {
            console.log('route path has changed from ' +from+' to '+to )
            let watchPath = this.videoModel ? `/watch/${this.videoModel.id}` : null
            if (this.hasPlayed && from == watchPath) {
                this.incrementViews();
            } else {
                console.log('No need to increment views', this.hasPlayed, watchPath);
            }

            if (from.includes("/watch") && to.includes("/watch")) {
                console.log('Reloading data')
                this.reloadData()
            }
        })
    },
    beforeUnmount() {
        if (this.autoplayTimer) {
            clearInterval(this.autoplayTimer);
            this.autoplayTimer = null;
        }
        if (this.mediaRepairPollTimer) {
            clearInterval(this.mediaRepairPollTimer);
            this.mediaRepairPollTimer = null;
        }
    },
    data() {
        return {
            newComment: null,
            currentUser: null,
            currentUserModel: null,
            videoModel: null,
            liked: null,
            disliked: null,
            likeInteractionId: null,
            dislikeInteractionId: null,
            loading: false,
            isLoadingData: true,
            videoNotFound: false,
            videoLoadError: false,
            posted: false,
            watched: false,
            subscription: null,
            userId: null,
            hasPlayed: false,
            currentTimePercentage: 0,
            videoDuration: 0,
            relatedVideos: [],
            recommendedVideos: [],
            loadedRelatedVideos: false,
            loadedRecommendedVideos: false,
            notificationsEnabled: false,
            showFullDescription: false,
            descriptionNeedsTruncation: false,
            replyingTo: null,
            replyText: '',
            commentInteractions: {},
            expandedReplies: {},
            theaterMode: localStorage.getItem('theaterMode') === 'true',
            shareCopied: false,
            autoplayEnabled: localStorage.getItem('autoplayEnabled') !== 'false',
            autoplayCountdown: 5,
            autoplayTimer: null,
            showAutoplayOverlay: false,
            canEditCurrentVideo: false,
            mediaLoadFailed: false,
            mediaFailureKind: '',
            mediaProcessingReadyHandled: false,
            mediaRepairPollTimer: null,
            videoPlayerKey: Date.now(),
            missingVideoFile: null,
            missingVideoSourceUrl: '',
            missingVideoUploading: false,
            missingVideoPreparing: false,
            missingVideoUploadProgress: 0,
            missingVideoUploadStatus: '',
            missingVideoUploadMessage: '',
        }
    },
    computed: {
        currentLanguage() {
            return this.languageRef?.value || this.languageRef || 'EN';
        },
        videoURL() {
            const separator = (url) => url.includes('?') ? '&' : '?';
            // Use videoUrl from legacy data if available
            if (this.videoModel?.videoUrl) {
                const url = `https://cdn.eelam.tv/${this.videoModel.videoUrl}`;
                return `${url}${separator(url)}v=${this.videoPlayerKey}`;
            }
            // Fallback to CDN URL
            const url = `https://cdn1.eelam.tv/public/videos/processed/${this.videoId}/${this.videoId}.m3u8`;
            return `${url}${separator(url)}v=${this.videoPlayerKey}`;
        },
        imageURL() {
            // Use thumbnailUrl from legacy data if available
            if (this.videoModel?.thumbnailUrl) {
                return `https://cdn.eelam.tv/${this.videoModel.thumbnailUrl}`;
            }
            // Fallback to CDN URL
            const imageIndex = this.videoModel?.imageName || '0';
            return `https://cdn1.eelam.tv/public/thumbs/${this.videoId}_${imageIndex}.jpeg`;
        },
        subscribersCount() {
            return this.videoModel.owner.subscribersCount ? this.videoModel.owner.subscribersCount : 0
        },
        sortedComments() {
            if (!this.videoModel?.comments?.items) return [];
            return [...this.videoModel.comments.items]
                .filter(comment => !comment.replyToId)
                .sort((a, b) => {
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });
        },
        videoId() {
            // Handle legacy URLs passed as prop: /watch/title-slug_videoId.html
            if (this.legacySlug) {
                const lastUnderscoreIndex = this.legacySlug.lastIndexOf('_');
                if (lastUnderscoreIndex !== -1) {
                    return this.legacySlug.substring(lastUnderscoreIndex + 1);
                }
                return this.legacySlug;
            }

            let id = this.$route.params.id;
            if (!id) return null;

            // Handle legacy URLs caught by /watch/:id route
            // Format: title-slug_videoId.html
            if (id.endsWith('.html') && id.includes('_')) {
                // Remove .html extension
                const withoutExt = id.slice(0, -5);
                // Extract video ID (after the last underscore)
                const lastUnderscoreIndex = withoutExt.lastIndexOf('_');
                if (lastUnderscoreIndex !== -1) {
                    return withoutExt.substring(lastUnderscoreIndex + 1);
                }
            }

            // Standard URL: /watch/:id or /v/:id
            return id;
        },
        allSidebarVideos() {
            // Combine related and recommended videos, removing duplicates
            const allVideos = [...this.relatedVideos, ...this.recommendedVideos];
            const uniqueVideos = [];
            const seenIds = new Set();

            for (const video of allVideos) {
                if (!seenIds.has(video.id)) {
                    seenIds.add(video.id);
                    uniqueVideos.push(video);
                }
            }

            // Sort: same category first, then others — shuffled within each group
            const shuffle = (arr) => {
                for (let i = arr.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [arr[i], arr[j]] = [arr[j], arr[i]];
                }
                return arr;
            };
            const categoryId = this.videoModel?.videoModelPrimaryCategoryId;
            const sameCategory = shuffle(uniqueVideos
                .filter(v => categoryId && v.videoModelPrimaryCategoryId === categoryId));
            const otherVideos = shuffle(uniqueVideos
                .filter(v => !categoryId || v.videoModelPrimaryCategoryId !== categoryId));
            return [...sameCategory, ...otherVideos];
        },
        isLoadingSidebarVideos() {
            return !this.loadedRelatedVideos || !this.loadedRecommendedVideos;
        },
        showBrokenMediaUpload() {
            return this.mediaFailureKind === 'missing' && this.videoModel && Boolean(this.userId);
        },
        showPlaybackError() {
            return this.mediaLoadFailed && !this.showBrokenMediaUpload;
        },
        showDownloadLink() {
            return Boolean(this.userId && this.videoModel && !this.mediaLoadFailed && this.videoModel.status !== 'failed');
        },
        downloadURL() {
            return `https://cdn1.eelam.tv/public/videos/raw/${this.videoId}.mp4`;
        },
        nextVideo() {
            return this.allSidebarVideos.length > 0 ? this.allSidebarVideos[0] : null;
        },
        currentUserAvatar() {
            const userModel = this.currentUserModel || this.$parent?.currentUser?.model;
            if (userModel?.legacyAvatar) {
                if (userModel.legacyAvatar.startsWith('public/')) {
                    return `https://cdn1.eelam.tv/${userModel.legacyAvatar}`;
                }
                return `https://cdn.eelam.tv/${userModel.legacyAvatar}`;
            }
            const userId = userModel?.id || this.userId;
            if (userId) {
                return `${this.cdnDomain}/public/images/user/${userId}/avatar.png`;
            }
            if (this.currentUser?.picture) {
                return this.currentUser.picture;
            }
            return '/avatar-placeholder.svg';
        },
        brokenMediaUploadCopy() {
            if (this.canSubmitMediaRepairDirectly()) {
                return this.currentLanguage === 'TA'
                    ? 'இந்த காணொளி தற்போது எங்கள் சேவையகத்தில் இல்லை. உங்கள் கணினியில் இந்த காணொளி இருந்தால், சமூகத்துடன் பகிர பதிவேற்றுங்கள்.'
                    : 'This video is currently missing from our server. If you have a copy, upload it to help restore it for the community.';
            }

            return this.currentLanguage === 'TA'
                ? 'இந்த காணொளி தற்போது எங்கள் சேவையகத்தில் இல்லை. உங்கள் கணினியில் இந்த காணொளி இருந்தால், சமூகத்துடன் பகிர பதிவேற்றுங்கள். நிர்வாகி சரிபார்த்து ஒப்புதல் அளித்த பிறகு அது அனைவருக்கும் காட்டப்படும்.'
                : 'This video is currently missing from our server. If you have a copy, upload it to help restore it for the community. It will appear for everyone once an admin reviews and approves it.';
        },
        videoStatusClass() {
            const status = this.videoModel?.status || 'ready';
            if (status === 'failed') return 'status-failed';
            if (this.videoModel?.publishState === 'pending') return 'status-pending';
            if (status === 'ready') return 'status-ready';
            if (status === 'processingReady') return 'status-playable';
            return 'status-processing';
        },
        videoStatusLabel() {
            const status = this.videoModel?.status || 'ready';
            const labels = {
                ready: this.currentLanguage === 'TA' ? 'தயார்' : 'Ready',
                processingReady: this.currentLanguage === 'TA' ? 'பார்க்க தயார்' : 'Playable',
                thumbsReady: this.currentLanguage === 'TA' ? 'சிறுபடம் தயார்' : 'Thumbnails ready',
                processing: this.currentLanguage === 'TA' ? 'செயலாக்கம்' : 'Processing',
                failed: this.currentLanguage === 'TA' ? 'தோல்வி' : 'Failed'
            };
            return labels[status] || status;
        },
        videoPublishStateLabel() {
            const publishState = this.videoModel?.publishState || 'published';
            const labels = {
                published: this.currentLanguage === 'TA' ? 'வெளியிடப்பட்டது' : 'Published',
                pending: this.currentLanguage === 'TA' ? 'நிலுவை' : 'Pending',
                draft: this.currentLanguage === 'TA' ? 'வரைவு' : 'Draft',
                private: this.currentLanguage === 'TA' ? 'தனிப்பட்டது' : 'Private',
                rejected: this.currentLanguage === 'TA' ? 'நிராகரிக்கப்பட்டது' : 'Rejected'
            };
            return labels[publishState] || publishState;
        },
        videoPublishStateClass() {
            const publishState = this.videoModel?.publishState || 'published';
            const classes = {
                published: 'publish-published',
                pending: 'publish-pending',
                draft: 'publish-draft',
                private: 'publish-private',
                rejected: 'publish-rejected'
            };
            return classes[publishState] || 'publish-draft';
        }
    },
    methods: {
        formatDescription(text) {
            if (!text) return '';
            let s = text;
            // Decode URI-encoded text
            try { s = decodeURIComponent(s); } catch(e) { /* ignore malformed URI */ }
            // [url=...]...[/url]
            s = s.replace(/\[url=([^\]]+)\](.*?)\[\/url\]/gi, '<a href="$1" target="_blank" rel="noopener">$2</a>');
            // [url]...[/url]
            s = s.replace(/\[url\](.*?)\[\/url\]/gi, '<a href="$1" target="_blank" rel="noopener">$1</a>');
            // [a]...[/a]
            s = s.replace(/\[a\](.*?)\[\/a\]/gi, '<a href="$1" target="_blank" rel="noopener">$1</a>');
            // [b]...[/b]
            s = s.replace(/\[b\](.*?)\[\/b\]/gi, '<b>$1</b>');
            // [i]...[/i]
            s = s.replace(/\[i\](.*?)\[\/i\]/gi, '<i>$1</i>');
            // [img]...[/img]
            s = s.replace(/\[img\](.*?)\[\/img\]/gi, '<img src="$1" style="max-width:100%" />');
            // Strip any remaining BBCode tags
            s = s.replace(/\[\/?[a-z]+(?:=[^\]]+)?\]/gi, '');
            // Newlines to <br>
            s = s.replace(/\n/g, '<br />');
            return s;
        },
        toggleTheaterMode() {
            this.theaterMode = !this.theaterMode;
            localStorage.setItem('theaterMode', this.theaterMode);
            analyticsTrack('theater_mode_toggle', { enabled: this.theaterMode });
        },
        reloadData() {
            this.cancelAutoplay();
            // Reset per-video view tracking so each video counts once. The router
            // reuses this component across /watch/:id routes, so without this the
            // flags stay true after the first video and no later video is counted.
            this.watched = false;
            this.hasPlayed = false;
            this.fetchVideoDetails()
        },
        handleMissingVideoFileChange(event) {
            const file = event.target.files?.[0] || null;
            this.missingVideoFile = file;
            this.missingVideoUploadStatus = '';
            this.missingVideoUploadMessage = '';
            this.missingVideoUploadProgress = 0;
        },
        handleVideoLoadError(error) {
            console.error('Video media failed to load', error);
            this.mediaLoadFailed = true;
            this.mediaFailureKind = this.getMediaFailureKind(error);
            analyticsTrack('video_media_load_failed', {
                video_id: this.videoId,
                video_url: this.videoURL,
                failure_kind: this.mediaFailureKind
            });
        },
        handleVideoLoaded() {
            this.mediaLoadFailed = false;
            this.mediaFailureKind = '';
        },
        getMediaFailureKind(error) {
            const message = [
                error?.message,
                error?.target?.error?.message,
                error?.target?.player?.error?.()?.message,
                error?.target?.player?.error?.()?.code,
                error?.target?.error?.code
            ].filter(Boolean).join(' ').toLowerCase();

            if (message.includes('404') || message.includes('not found') || message.includes('network_no_source')) {
                return 'missing';
            }

            return 'playback';
        },
        isVerifiedCurrentUser() {
            const userModel = this.currentUserModel || this.$parent?.currentUser?.model;
            return userModel?.status === 'verified';
        },
        canSubmitMediaRepairDirectly() {
            return this.canEditCurrentVideo || this.isVerifiedCurrentUser();
        },
        async submitBrokenMediaUpload() {
            const file = this.missingVideoFile;
            const videoId = this.videoId?.trim();

            if (!file || !videoId || !this.videoModel) {
                this.missingVideoUploadStatus = 'error';
                this.missingVideoUploadMessage = this.currentLanguage === 'TA'
                    ? 'காணொளி கோப்பைத் தேர்ந்தெடுக்கவும்.'
                    : 'Please choose a video file.';
                return;
            }

            if (file.type && !file.type.startsWith('video/')) {
                this.missingVideoUploadStatus = 'error';
                this.missingVideoUploadMessage = this.currentLanguage === 'TA'
                    ? 'சரியான காணொளி கோப்பைத் தேர்ந்தெடுக்கவும்.'
                    : 'Please choose a valid video file.';
                return;
            }

            this.missingVideoUploading = true;
            this.missingVideoPreparing = false;
            this.mediaProcessingReadyHandled = false;
            this.missingVideoUploadProgress = 0;
            this.missingVideoUploadStatus = '';
            this.missingVideoUploadMessage = '';

            try {
                const canRepairDirectly = this.canSubmitMediaRepairDirectly();
                analyticsTrack('broken_media_replacement_upload_start', {
                    video_id: videoId,
                    file_size: file.size,
                    file_type: file.type,
                    requires_review: !canRepairDirectly
                });

                const uploadKey = canRepairDirectly
                    ? `videos/raw/${videoId}.mp4`
                    : `videos/replacement-requests/${videoId}-${Date.now()}.mp4`;

                await this.uploadReplacementFile(uploadKey, file, videoId, canRepairDirectly);

                this.missingVideoUploading = false;
                this.missingVideoPreparing = canRepairDirectly;
                this.missingVideoUploadProgress = 100;
                this.missingVideoUploadStatus = 'success';
                this.missingVideoUploadMessage = this.currentLanguage === 'TA'
                    ? (canRepairDirectly
                        ? 'பதிவேற்றம் முடிந்தது. காணொளி செயலாக்கப்படுகிறது.'
                        : 'பதிவேற்றம் பெறப்பட்டது. நிர்வாகி சரிபார்த்த பிறகு காணொளி மீண்டும் காட்டப்படும்.')
                    : (canRepairDirectly
                        ? 'Upload complete. The video is processing now.'
                        : 'Upload received. The video will return after an admin reviews it.');

                if (canRepairDirectly) {
                    this.videoModel.status = 'processing';
                    this.pollMediaRepairProcessing();
                } else {
                    this.missingVideoFile = null;
                }

                analyticsTrack('broken_media_replacement_upload_complete', {
                    video_id: videoId,
                    requires_review: !canRepairDirectly
                });
            } catch (error) {
                console.error('Failed to upload replacement video', error);
                this.missingVideoUploading = false;
                this.missingVideoPreparing = false;
                this.missingVideoUploadStatus = 'error';
                this.missingVideoUploadMessage = this.getMediaRepairUploadError(error);
                analyticsTrack('broken_media_replacement_upload_failed', {
                    video_id: videoId,
                    error: error.message || String(error)
                });
            }
        },
        uploadReplacementFile(uploadKey, file, videoId, canRepairDirectly = false) {
            return new Promise((resolve, reject) => {
                Storage.put(uploadKey, file, {
                    resumable: true,
                    contentType: file.type || 'video/mp4',
                    metadata: {
                        videoid: videoId,
                        requireapproval: canRepairDirectly ? 'false' : 'true',
                        requestedby: this.userId || 'unknown'
                    },
                    progressCallback: (progress) => {
                        if (progress.total) {
                            this.missingVideoUploadProgress = (progress.loaded / progress.total) * 100;
                        }
                    },
                    completeCallback: (event) => {
                        this.missingVideoUploadProgress = 100;
                        resolve(event);
                    },
                    errorCallback: reject
                });
            });
        },
        async submitBrokenMediaUrl() {
            const videoId = this.videoId?.trim();
            const sourceUrl = this.missingVideoSourceUrl;

            if (!videoId || !this.videoModel || !sourceUrl) {
                this.missingVideoUploadStatus = 'error';
                this.missingVideoUploadMessage = this.currentLanguage === 'TA'
                    ? 'காணொளி URL-ஐ உள்ளிடவும்.'
                    : 'Please enter a video URL.';
                return;
            }

            let parsedUrl;
            try {
                parsedUrl = new URL(sourceUrl);
            } catch (error) {
                this.missingVideoUploadStatus = 'error';
                this.missingVideoUploadMessage = this.currentLanguage === 'TA'
                    ? 'சரியான URL-ஐ உள்ளிடவும்.'
                    : 'Please enter a valid URL.';
                return;
            }

            if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
                this.missingVideoUploadStatus = 'error';
                this.missingVideoUploadMessage = this.currentLanguage === 'TA'
                    ? 'HTTP அல்லது HTTPS URL மட்டும் பயன்படுத்தவும்.'
                    : 'Only HTTP or HTTPS URLs are supported.';
                return;
            }

            this.missingVideoUploading = true;
            this.missingVideoPreparing = false;
            this.mediaProcessingReadyHandled = false;
            this.missingVideoUploadProgress = 0;
            this.missingVideoUploadStatus = '';
            this.missingVideoUploadMessage = this.currentLanguage === 'TA'
                ? 'URL செயலாக்க கோரிக்கை அனுப்பப்படுகிறது.'
                : 'Submitting URL for processing.';

            try {
                const canRepairDirectly = this.canSubmitMediaRepairDirectly();
                const requestKey = `videos/url-requests/${videoId}-${Date.now()}.json`;
                await Storage.put(requestKey, JSON.stringify({
                    videoId,
                    sourceUrl,
                    requireApproval: !canRepairDirectly,
                    requestedAt: new Date().toISOString(),
                }), {
                    contentType: 'application/json',
                });

                this.missingVideoUploading = false;
                this.missingVideoPreparing = canRepairDirectly;
                this.missingVideoUploadProgress = 100;
                this.missingVideoUploadStatus = 'success';
                this.missingVideoUploadMessage = this.currentLanguage === 'TA'
                    ? (canRepairDirectly
                        ? 'URL பெறப்பட்டது. காணொளி பதிவிறக்கம் செய்து செயலாக்கப்படுகிறது.'
                        : 'URL பெறப்பட்டது. நிர்வாகி சரிபார்த்த பிறகு காணொளி மீண்டும் காட்டப்படும்.')
                    : (canRepairDirectly
                        ? 'URL received. The video will be downloaded and processed now.'
                        : 'URL received. The video will return after an admin reviews it.');

                if (canRepairDirectly) {
                    this.pollMediaRepairProcessing();
                } else {
                    this.missingVideoSourceUrl = '';
                }

                analyticsTrack('broken_media_url_submitted', {
                    video_id: videoId,
                    source_host: parsedUrl.hostname,
                    requires_review: !canRepairDirectly
                });
            } catch (error) {
                console.error('Failed to submit video URL', error);
                this.missingVideoUploading = false;
                this.missingVideoPreparing = false;
                this.missingVideoUploadStatus = 'error';
                this.missingVideoUploadMessage = this.getMediaRepairUploadError(error);
                analyticsTrack('broken_media_url_submit_failed', {
                    video_id: videoId,
                    error: error.message || String(error),
                });
            }
        },
        pollMediaRepairProcessing() {
            if (this.mediaRepairPollTimer) {
                clearInterval(this.mediaRepairPollTimer);
            }

            const startTime = Date.now();
            const pollInterval = 5000;
            const pollTimeout = 10 * 60 * 1000;

            this.mediaRepairPollTimer = setInterval(async () => {
                try {
                    const res = await API.graphql({
                        query: getVideoProcessingStatus,
                        variables: { id: this.videoId.trim() }
                    });
                    const video = res.data.getVideoModel;
                    const status = video?.status;

                    if (status === 'thumbsReady') {
                        this.missingVideoUploadMessage = this.currentLanguage === 'TA'
                            ? 'சிறுபடங்கள் தயாராகின்றன. இறுதி செயலாக்கம் தொடர்கிறது.'
                            : 'Thumbnails are ready. Final processing is continuing.';
                    }

                    if (status === 'processingReady' && !this.mediaProcessingReadyHandled) {
                        this.mediaProcessingReadyHandled = true;
                        this.missingVideoPreparing = false;
                        this.mediaLoadFailed = false;
                        this.mediaFailureKind = '';
                        this.videoPlayerKey = Date.now();
                        this.missingVideoUploadStatus = 'success';
                        this.missingVideoUploadMessage = this.currentLanguage === 'TA'
                            ? 'காணொளி பார்க்க தயாராக உள்ளது. உயர் தர பதிப்புகள் இன்னும் செயலாக்கப்படுகின்றன.'
                            : 'The video is playable now. Higher quality versions are still processing.';
                        this.reloadData();
                    }

                    if (status === 'ready') {
                        clearInterval(this.mediaRepairPollTimer);
                        this.mediaRepairPollTimer = null;
                        this.missingVideoPreparing = false;
                        this.mediaLoadFailed = false;
                        this.mediaFailureKind = '';
                        this.videoPlayerKey = Date.now();
                        this.missingVideoUploadStatus = 'success';
                        this.missingVideoUploadMessage = this.currentLanguage === 'TA'
                            ? 'காணொளி முழுமையாக தயாராக உள்ளது.'
                            : 'Replacement video is fully ready.';
                        this.reloadData();
                    }

                    if (status === 'failed') {
                        clearInterval(this.mediaRepairPollTimer);
                        this.mediaRepairPollTimer = null;
                        this.missingVideoPreparing = false;
                        this.missingVideoUploadStatus = 'error';
                        this.missingVideoUploadMessage = video?.errorMessage || (this.currentLanguage === 'TA'
                            ? 'செயலாக்கம் தோல்வியடைந்தது.'
                            : 'Video processing failed.');
                    }

                    if (Date.now() - startTime > pollTimeout) {
                        clearInterval(this.mediaRepairPollTimer);
                        this.mediaRepairPollTimer = null;
                        this.missingVideoPreparing = false;
                        this.missingVideoUploadMessage = this.currentLanguage === 'TA'
                            ? 'செயலாக்கம் இன்னும் நடக்கிறது. சிறிது நேரத்தில் பக்கத்தை புதுப்பிக்கவும்.'
                            : 'Processing is taking longer than expected. Please refresh in a few minutes.';
                    }
                } catch (error) {
                    console.error('Error polling replacement video processing:', error);
                }
            }, pollInterval);
        },
        getMediaRepairUploadError(error) {
            const message = error?.errors?.[0]?.message || error?.message || '';
            if (message.includes('Network') || message.includes('Failed to fetch')) {
                return this.currentLanguage === 'TA'
                    ? 'இணைப்பு சிக்கல் ஏற்பட்டது. மீண்டும் முயற்சிக்கவும்.'
                    : 'A network error occurred. Please try again.';
            }
            return this.currentLanguage === 'TA'
                ? 'பதிவேற்றம் தோல்வியடைந்தது. மீண்டும் முயற்சிக்கவும்.'
                : 'Upload failed. Please try again.';
        },
        togglePlaying() {
            this.hasPlayed = true
            analyticsTrack('video_play', {
                video_id: this.videoId,
                title: this.videoModel?.titleEN || this.videoModel?.titleTA,
                owner_id: this.videoModel?.owner?.id,
                duration: this.videoModel?.duration,
                category: this.videoModel?.primaryCategory?.nameEN,
            });
            this.incrementViews()
        },
        updateCurrentTime(time, data) {
            console.log('Video current time', time, data)
        },
        updateTimeDuration(duration) {
            console.log('Video duration', duration)
            this.videoDuration = duration
        },
        async fetchVideoDetails() {
            var videoId = this.videoId
            this.isLoadingData = true
            this.videoNotFound = false
            this.videoLoadError = false
            this.videoModel = null
            this.canEditCurrentVideo = false
            this.mediaLoadFailed = false
            this.mediaFailureKind = ''
            this.mediaProcessingReadyHandled = false
            this.missingVideoFile = null
            this.missingVideoSourceUrl = ''
            this.missingVideoUploading = false
            this.missingVideoPreparing = false
            this.missingVideoUploadProgress = 0
            this.missingVideoUploadStatus = ''
            this.missingVideoUploadMessage = ''

            if (!videoId) {
                this.videoNotFound = true
                document.title = 'Video Not Found - Eelam TV'
                this.isLoadingData = false
                return;
            }

            try {
                const videoRes = await API.graphql({
                    query: getVideoModelWithLikesAndDislikes,
                    variables: { id: videoId.trim() },
                });
                this.videoModel = videoRes.data.getVideoModel

                if (!this.videoModel) {
                    this.videoNotFound = true
                    document.title = 'Video Not Found - Eelam TV'
                    return;
                }

                await this.checkEditAuthorization()

                // Drafts stay publicly viewable on a direct link so previously-indexed
                // pages aren't dropped from search results (SEO). Other non-published
                // states (pending review, private, rejected) remain owner/admin-only.
                const publishState = this.videoModel.publishState
                const publiclyViewable = !publishState || publishState === 'published' || publishState === 'draft'
                if (!publiclyViewable && !this.canEditCurrentVideo) {
                    this.videoModel = null
                    this.videoNotFound = true
                    document.title = 'Video Not Found - Eelam TV'
                    return;
                }

                // Update page title
                const videoTitle = this.decodeHtml(this.videoModel.titleEN || this.videoModel.titleTA || 'Video')
                document.title = `${videoTitle} - Eelam TV`
                // Check user's interaction with this video using separate GSI query
                if (this.userId) {
                    this.fetchUserInteraction()
                    this.fetchCommentInteractions()
                }
                this.fetchSubscriptionStatus()
                this.fetchRelatedVideos()
                console.log("Got Video", this.videoModel)
                
                // Check if description needs truncation after data is loaded
                this.$nextTick(() => {
                    this.checkDescriptionTruncation();
                });
            } catch (error) {
                console.error('Failed to fetch video details', error);
                captureError(error, { source: 'api_fetch', component: 'VideoDetail', operation: 'fetchVideoDetails', videoId })
                // An API/network failure is not "video not found" — show a
                // retryable error instead of the misleading not-found page.
                this.videoLoadError = true
                document.title = 'Eelam TV'
            } finally {
                this.isLoadingData = false
            }
        },
        async checkEditAuthorization() {
            this.canEditCurrentVideo = this.videoModel ? await canEditVideo(this.videoModel) : false;
        },
        async fetchUserInteraction() {
            try {
                const videoId = this.videoId.trim();
                console.log('fetchUserInteraction:', { userId: this.userId, videoId });

                const checkQuery = /* GraphQL */ `
                    query InteractionByUserAndVideo($userId: ID!, $videoId: ModelIDKeyConditionInput) {
                        interactionByUserAndVideo(userId: $userId, videoId: $videoId) {
                            items { id type userId videoId }
                        }
                    }
                `;
                const res = await API.graphql({
                    query: checkQuery,
                    variables: {
                        userId: this.userId,
                        videoId: { eq: videoId }
                    }
                });
                const items = res.data.interactionByUserAndVideo?.items || [];
                console.log('fetchUserInteraction results:', items.length, 'items', items);
                const likeInteraction = items.find(i => i.type === 'like');
                const dislikeInteraction = items.find(i => i.type === 'dislike');
                this.liked = !!likeInteraction;
                this.disliked = !!dislikeInteraction;
                this.likeInteractionId = likeInteraction?.id || null;
                this.dislikeInteractionId = dislikeInteraction?.id || null;
                console.log('User interaction check:', { liked: this.liked, disliked: this.disliked });
            } catch (error) {
                console.error('Error fetching user interaction:', error);
            }
        },
        async fetchCommentInteractions() {
            try {
                const videoId = this.videoId.trim();
                const res = await API.graphql({
                    query: commentInteractionsByUserAndVideo,
                    variables: {
                        userId: this.userId,
                        videoId: { eq: videoId }
                    }
                });
                const items = res.data.commentInteractionByUserAndVideo?.items || [];
                const map = {};
                for (const item of items) {
                    if (!map[item.commentId]) {
                        map[item.commentId] = { liked: false, disliked: false, likeId: null, dislikeId: null };
                    }
                    if (item.type === 'like') {
                        map[item.commentId].liked = true;
                        map[item.commentId].likeId = item.id;
                    } else if (item.type === 'dislike') {
                        map[item.commentId].disliked = true;
                        map[item.commentId].dislikeId = item.id;
                    }
                }
                this.commentInteractions = map;
                console.log('Comment interactions loaded:', Object.keys(map).length, 'comments');
            } catch (error) {
                console.error('Error fetching comment interactions:', error);
            }
        },
        async fetchSubscriptionStatus() {
            if (!this.userId) return;
            console.log('Checking subscription:', this.userId, '->', this.videoModel.owner.id)
            const videoRes = await API.graphql({
                query: checkSubscription,
                variables: {
                    subscriberId: this.userId,
                    subscribedToId: { eq: this.videoModel.owner.id }
                },
            });
            const items = videoRes.data.subscribersBySubscriberId?.items || [];
            this.subscription = items.length ? items[0] : null
            // Set notification state based on subscription
            this.notificationsEnabled = this.subscription ? this.subscription.notify : false
            console.log("fetchSubscriptionStatus response", videoRes)
        },
        async likeVideo() {
            var videoId = this.videoId
            var userId = this.userId
            if (!videoId || !userId) return;

            analyticsTrack('video_like', { video_id: videoId });

            // Optimistic UI update
            this.liked = true
            this.videoModel.likesCount = (this.videoModel.likesCount ?? 0) + 1

            // If currently disliked, undo dislike first
            if (this.disliked) {
                this.disliked = false
                this.videoModel.dislikesCount = Math.max((this.videoModel.dislikesCount ?? 0) - 1, 0)
                if (this.dislikeInteractionId) {
                    API.graphql({
                        query: deleteInteractionSimple,
                        variables: { input: { id: this.dislikeInteractionId } },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    }).catch(e => console.error('Error removing dislike:', e))
                    this.dislikeInteractionId = null
                }
            }

            try {
                const input = { videoId: videoId.trim(), userId, type: 'like', createdAt: new Date().toISOString() };
                console.log("Creating like interaction:", input);
                const videoRes = await API.graphql({
                    query: createInteractionSimple,
                    variables: { input },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                console.log("Like created:", videoRes.data.createVideoInteractionModel);
                this.likeInteractionId = videoRes.data.createVideoInteractionModel.id
            } catch (error) {
                // Revert optimistic update on failure
                this.liked = false
                this.videoModel.likesCount = Math.max((this.videoModel.likesCount ?? 0) - 1, 0)
                console.error("Error liking video:", error)
                this.$toast.error('Failed to like video. Please try again.')
            }
        },
        async dislikeVideo() {
            var videoId = this.videoId
            var userId = this.userId
            if (!videoId || !userId) return;

            analyticsTrack('video_dislike', { video_id: videoId });

            // Optimistic UI update
            this.disliked = true
            this.videoModel.dislikesCount = (this.videoModel.dislikesCount ?? 0) + 1

            // If currently liked, undo like first
            if (this.liked) {
                this.liked = false
                this.videoModel.likesCount = Math.max((this.videoModel.likesCount ?? 0) - 1, 0)
                if (this.likeInteractionId) {
                    API.graphql({
                        query: deleteInteractionSimple,
                        variables: { input: { id: this.likeInteractionId } },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    }).catch(e => console.error('Error removing like:', e))
                    this.likeInteractionId = null
                }
            }

            try {
                const input = { videoId: videoId.trim(), userId, type: 'dislike', createdAt: new Date().toISOString() };
                const videoRes = await API.graphql({
                    query: createInteractionSimple,
                    variables: { input },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                this.dislikeInteractionId = videoRes.data.createVideoInteractionModel.id
                console.log("Dislike created:", videoRes.data.createVideoInteractionModel)
            } catch (error) {
                // Revert optimistic update on failure
                this.disliked = false
                this.videoModel.dislikesCount = Math.max((this.videoModel.dislikesCount ?? 0) - 1, 0)
                console.error("Error disliking video:", error)
                this.$toast.error('Failed to dislike video. Please try again.')
            }
        },
        async postComment() {
            var videoId = this.videoId
            // Get userId from parent's model (UUID) or fall back to stored userId
            var userId = this.$parent?.currentUser?.model?.id || this.userId
            console.log('postComment - using userId:', userId)
            if (!videoId || !userId) return;

            const commentInput = {
                videoId: videoId.trim(),
                ownerId: userId,
                message: this.newComment
            };

            this.loading = true
            try {
                const commentRes = await API.graphql({
                    query: createCommentModel,
                    variables: { input: commentInput },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                this.videoModel.comments.items.push(commentRes.data.createCommentModel)
                this.videoModel.commentsCount = (this.videoModel.commentsCount || 0) + 1
                this.posted = true
                this.newComment = null
                analyticsTrack('comment_post', { video_id: videoId });
                console.log("Comment res", commentRes.data.createCommentModel)
            } catch (error) {
                console.error("Error posting comment:", error);
                this.$toast.error('Failed to post comment. Please try again.');
            }
            this.loading = false
        },
        guestId() {
            // Stable anonymous identifier so guest views can be recorded and deduped.
            let id = localStorage.getItem('guestId')
            if (!id) {
                const uuid = (window.crypto && window.crypto.randomUUID)
                    ? window.crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
                id = `guest_${uuid}`
                localStorage.setItem('guestId', id)
            }
            return id
        },
        async incrementViews() {
            var videoId = this.videoModel.id
            if (!videoId || this.watched) return;

            analyticsTrack('video_view', { video_id: videoId });

            // Optimistic UI update
            this.videoModel.viewsCount = (this.videoModel.viewsCount ?? 0) + 1

            // Create watch interaction — Lambda stream handles counter updates
            await this.updateHistory()

            this.watched = true
        },
        async updateHistory() {
            var videoId = this.videoModel.id
            if (!videoId) return;

            const isGuest = !this.userId;

            // Client-side dedup for guests: only count each video once per browser.
            // (Authenticated users are deduped server-side / by watch history.)
            if (isGuest) {
                const seen = JSON.parse(localStorage.getItem('guestWatched') || '[]');
                if (seen.includes(videoId)) return;
                localStorage.setItem('guestWatched', JSON.stringify([...seen, videoId].slice(-500)));
            }

            const videoIdInput = {
                videoId: videoId.trim(),
                userId: isGuest ? this.guestId() : this.userId,
                type: 'watch',
                watchPercentage: this.currentTimePercentage,
                createdAt: new Date().toISOString(),
            };

            const videoRes = await API.graphql({
                query: createInteractionSimple,
                variables: { input: videoIdInput },
                authMode: isGuest ? 'API_KEY' : 'AMAZON_COGNITO_USER_POOLS'
            });
            console.log("Updated views history res", videoRes)
        },
        async unLikeVideo() {
            if (!this.likeInteractionId || !this.liked) return;

            analyticsTrack('video_unlike', { video_id: this.videoId });

            // Optimistic UI update
            this.liked = false
            this.videoModel.likesCount = Math.max((this.videoModel.likesCount ?? 0) - 1, 0)
            const interactionId = this.likeInteractionId
            this.likeInteractionId = null

            try {
                await API.graphql({
                    query: deleteInteractionSimple,
                    variables: { input: { id: interactionId } },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                console.log("Unlike res - success")
            } catch (error) {
                // Revert on failure
                this.liked = true
                this.videoModel.likesCount = (this.videoModel.likesCount ?? 0) + 1
                this.likeInteractionId = interactionId
                console.error("Error unliking video:", error)
                this.$toast.error('Failed to unlike video. Please try again.')
            }
        },
        async unDislikeVideo() {
            if (!this.dislikeInteractionId || !this.disliked) return;

            analyticsTrack('video_undislike', { video_id: this.videoId });

            // Optimistic UI update
            this.disliked = false
            this.videoModel.dislikesCount = Math.max((this.videoModel.dislikesCount ?? 0) - 1, 0)
            const interactionId = this.dislikeInteractionId
            this.dislikeInteractionId = null

            try {
                await API.graphql({
                    query: deleteInteractionSimple,
                    variables: { input: { id: interactionId } },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                console.log("Undislike res - success")
            } catch (error) {
                // Revert on failure
                this.disliked = true
                this.videoModel.dislikesCount = (this.videoModel.dislikesCount ?? 0) + 1
                this.dislikeInteractionId = interactionId
                console.error("Error undisliking video:", error)
                this.$toast.error('Failed to update vote. Please try again.')
            }
        },
        async deleteComment(comment) {
            this.loading = true
            try {
                const videoIdInput = {
                    id: comment.id
                };
                const videoRes = await API.graphql({
                    query: deleteCommentModel,
                    variables: { input: videoIdInput },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                console.log('delete response', videoRes)

                this.videoModel.comments.items = this.videoModel.comments.items.filter(item =>
                    item.id !== comment.id
                )
                this.videoModel.commentsCount = Math.max(0, (this.videoModel.commentsCount || 0) - 1)
                analyticsTrack('comment_delete', { video_id: this.videoId, comment_id: comment.id });
            } catch (error) {
                console.error("Error deleting comment:", error);
                this.$toast.error('Failed to delete comment. Please try again.');
            }
            this.loading = false
        },
        async subscribeToggle() {
            this.loading = true

            try {
                if (this.subscription == null) {
                    analyticsTrack('channel_subscribe', { channel_id: this.videoModel.owner.id, channel_username: this.videoModel.owner.username });
                    console.log('gotta subscribe yo')
                    const subscriberInput = {
                        subscribedToId: this.videoModel.owner.id,
                        subscriberId: this.userId,
                        notify: false
                    };
                    const subscriptionRes = await API.graphql({
                        query: createUserSubscriber,
                        variables: { input: subscriberInput },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    });

                    this.videoModel.owner.subscribersCount = (this.videoModel.owner.subscribersCount || 0) + 1

                    this.subscription = subscriptionRes.data.createUserSubscriber
                    console.log('subscriber response', subscriptionRes)
                } else {
                    analyticsTrack('channel_unsubscribe', { channel_id: this.videoModel.owner.id });
                    console.log('gotta unsubscribe yo')
                    const subscriberInput = {
                        id: this.subscription.id
                    };
                    const subscriptionRes = await API.graphql({
                        query: deleteUserSubscriber,
                        variables: { input: subscriberInput },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    });

                    if (this.videoModel.owner.subscribersCount > 0) {
                        this.videoModel.owner.subscribersCount--
                    }

                    console.log('unsubscription res', subscriptionRes)
                    this.subscription = null
                }
            } catch (error) {
                console.error('Error toggling subscription:', error);
                this.$toast.error('Failed to update subscription. Please try again.');
            }
            this.loading = false

        },
        isOwner(comment) {
            return comment.owner && comment.owner.id == this.userId
        },
        async fetchRelatedVideos() {
            this.relatedVideos = []
            this.recommendedVideos = []
            this.loadedRelatedVideos = false
            this.loadedRecommendedVideos = false

            try {
                const res = await API.graphql({
                    query: getRecommendationsQuery,
                    variables: {
                        videoId: this.videoModel.id,
                        limit: 25
                    }
                });

                const items = res.data.getRecommendations?.items || [];
                // Map flat owner fields to nested owner object for VideoThumb compatibility
                const mapped = items.map(v => ({
                    ...v,
                    owner: {
                        id: v.ownerId,
                        username: v.ownerUsername,
                        displayName: v.ownerDisplayName,
                        legacyAvatar: v.ownerLegacyAvatar,
                    }
                })).filter(video => this.isPublicRelatedVideo(video));

                this.relatedVideos = mapped.slice(0, 15);
                this.recommendedVideos = mapped.slice(15);
                console.log(`Recommendations loaded (${items.length} items, source: ${res.data.getRecommendations?.source})`);
            } catch (error) {
                console.error('Error fetching recommendations from Lambda, falling back:', error);
                await this.fetchRelatedVideosFallback();
            }

            this.loadedRelatedVideos = true
            this.loadedRecommendedVideos = true
        },
        async fetchRelatedVideosFallback() {
            try {
                const res = await API.graphql({
                    query: videosByStatusWithOwner,
                    variables: {
                        status: 'ready',
                        sortDirection: 'DESC',
                        filter: { publishState: { eq: 'published' } },
                        limit: 50
                    }
                });
                const allVideos = (res.data.videosByStatus?.items || [])
                    .filter(v => v.id !== this.videoModel.id && this.isPublicRelatedVideo(v));

                const categoryId = this.videoModel.videoModelPrimaryCategoryId;
                const scored = allVideos.map(video => {
                    let score = 0;
                    if (categoryId && video.videoModelPrimaryCategoryId === categoryId) score += 20;
                    if (video.owner?.id === this.videoModel.owner?.id) score += 5;
                    score += Math.min((video.viewsCount || 0) / 500, 10);
                    const daysOld = (Date.now() - new Date(video.createdAt)) / 86400000;
                    score += Math.max(0, 15 - daysOld * 0.5);
                    score += Math.random() * 8;
                    return { ...video, _score: score };
                });
                scored.sort((a, b) => b._score - a._score);
                this.relatedVideos = scored.slice(0, 15);
                this.recommendedVideos = scored.slice(15, 25);
            } catch (err) {
                console.error('Fallback related videos also failed:', err);
            }
        },
        isPublicRelatedVideo(video) {
            return video &&
                (!video.publishState || video.publishState === 'published') &&
                (!video.status || ['ready', 'processingReady'].includes(video.status));
        },

        imageURLForVideo(videoId) {
            // Find the video in the related/recommended videos
            const video = this.allSidebarVideos.find(v => v.id === videoId);
            // Use thumbnailUrl from legacy data if available
            if (video?.thumbnailUrl) {
                return `https://cdn.eelam.tv/${video.thumbnailUrl}`;
            }
            // Fallback to CDN URL
            const imageIndex = video?.imageName || '0';
            return `https://cdn1.eelam.tv/public/thumbs/${videoId}_${imageIndex}.jpeg`;
        },
        handleThumbnailError(event) {
            if (event.target.src.endsWith('/video-placeholder.svg')) return;
            event.target.src = '/video-placeholder.svg';
        },
        formatNumber(num) {
            if (!num) return '0';
            if (num >= 1000000) {
                return (num / 1000000).toFixed(1) + 'M';
            } else if (num >= 1000) {
                return (num / 1000).toFixed(1) + 'K';
            }
            return num.toString();
        },
        autoResize(event) {
            const textarea = event.target;
            textarea.style.height = 'auto';
            textarea.style.height = textarea.scrollHeight + 'px';
        },
        userImage(userId) {
            return `${this.cdnDomain}/public/images/user/${userId}/avatar.png`;
        },
        getOwnerAvatar(owner) {
            if (!owner?.id) return '/avatar-placeholder.svg';
            if (owner.legacyAvatar) {
                if (owner.legacyAvatar.startsWith('public/')) {
                    return `https://cdn1.eelam.tv/${owner.legacyAvatar}`;
                }
                return `https://cdn.eelam.tv/${owner.legacyAvatar}`;
            }
            return `${this.cdnDomain}/public/images/user/${owner.id}/avatar.png`;
        },
        handleAvatarError(event) {
            if (event.target.src.endsWith('/avatar-placeholder.svg')) return;
            event.target.src = '/avatar-placeholder.svg';
        },
        displayName(user) {
            return user.displayName || user.username || 'Unknown User';
        },
        getVideoOwner(video) {
            // Return owner if exists, otherwise return dummy owner data
            if (video.owner && video.owner.id) {
                return video.owner;
            }
            // Create dummy owner for videos without an owner
            return {
                id: 'eelam-tv',
                username: 'eelam-tv',
                displayName: 'Eelam TV',
                firstName: 'Eelam',
                lastName: 'TV',
                status: 'unverified',
                emailVerified: false
            };
        },
        timeAgo(date) {
            const now = new Date();
            const diffInSeconds = Math.floor((now - date) / 1000);
            const ta = this.currentLanguage === 'TA';

            if (diffInSeconds < 60) {
                return ta ? 'இப்போது' : 'just now';
            }
            if (diffInSeconds < 3600) {
                const minutes = Math.floor(diffInSeconds / 60);
                return ta ? `${minutes} ${this.tamilPlural(minutes, 'நிமிடம்', 'நிமிடங்கள்')} முன்` : `${minutes}m ago`;
            }
            if (diffInSeconds < 86400) {
                const hours = Math.floor(diffInSeconds / 3600);
                return ta ? `${hours} ${this.tamilPlural(hours, 'மணித்தியாலம்', 'மணித்தியாலங்கள்')} முன்` : `${hours}h ago`;
            }
            if (diffInSeconds < 2592000) {
                const days = Math.floor(diffInSeconds / 86400);
                return ta ? `${days} ${this.tamilPlural(days, 'நாள்', 'நாட்கள்')} முன்` : `${days}d ago`;
            }
            if (diffInSeconds < 31536000) {
                const months = Math.floor(diffInSeconds / 2592000);
                return ta ? `${months} ${this.tamilPlural(months, 'மாதம்', 'மாதங்கள்')} முன்` : `${months}mo ago`;
            }
            const years = Math.floor(diffInSeconds / 31536000);
            return ta ? `${years} ${this.tamilPlural(years, 'ஆண்டு', 'ஆண்டுகள்')} முன்` : `${years}y ago`;
        },
        tamilPlural(count, singular, plural) {
            return Number(count) === 1 ? singular : plural;
        },
        formattedDuration(seconds) {
            if (!seconds) return '0:00';
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = seconds % 60;
            
            if (hours > 0) {
                return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            }
            return `${minutes}:${secs.toString().padStart(2, '0')}`;
        },
        formatSignificantDate(dateString) {
            if (!dateString) return '';
            // significantDate is an AWSDate (YYYY-MM-DD). `new Date('YYYY-MM-DD')`
            // parses as UTC midnight, which renders as the previous day in
            // negative-offset timezones. Parse the components as a LOCAL date.
            let date;
            const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
            if (dateOnly) {
                date = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
            } else {
                date = new Date(dateString);
            }

            // Format the date
            const options = {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            };
            const formattedDate = date.toLocaleDateString('en-US', options);

            // Relative label based on whole-calendar-day difference (past dates only).
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const diffDays = Math.round((today - date) / (1000 * 60 * 60 * 24));

            if (diffDays >= 0 && diffDays <= 365) {
                if (diffDays === 0) return `Today (${formattedDate})`;
                if (diffDays === 1) return `Yesterday (${formattedDate})`;
                if (diffDays < 7) return `${diffDays} days ago (${formattedDate})`;
                if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago (${formattedDate})`;
                return `${Math.floor(diffDays / 30)} months ago (${formattedDate})`;
            }

            return formattedDate;
        },
        toggleDescription() {
            this.showFullDescription = !this.showFullDescription;
        },
        checkDescriptionTruncation() {
            this.$nextTick(() => {
                if (this.$refs.descriptionText) {
                    const element = this.$refs.descriptionText;
                    this.descriptionNeedsTruncation = element.scrollHeight > element.clientHeight;
                }
            });
        },
        async toggleNotifications() {
            if (!this.subscription) {
                console.log('No subscription found, cannot toggle notifications');
                return;
            }

            this.loading = true;
            const newNotifyState = !this.notificationsEnabled;
            
            try {
                const updateInput = {
                    id: this.subscription.id,
                    notify: newNotifyState
                };
                
                const updateRes = await API.graphql({
                    query: updateUserSubscriber,
                    variables: { input: updateInput },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });

                // Update local state
                this.notificationsEnabled = newNotifyState;
                this.subscription = updateRes.data.updateUserSubscriber;
                analyticsTrack('notification_toggle', { channel_id: this.videoModel.owner.id, enabled: newNotifyState });
                console.log('Notifications updated:', newNotifyState);
            } catch (error) {
                console.error('Error updating notifications:', error);
                this.$toast.error('Failed to update notification settings.');
                // Revert the UI state if the update failed
                this.notificationsEnabled = !newNotifyState;
            } finally {
                this.loading = false;
            }
        },
        
        startReply(comment) {
            this.replyingTo = comment.id;
            this.replyText = '';
            this.$nextTick(() => {
                const el = this.$refs['replyInput-' + comment.id];
                if (el) {
                    const textarea = Array.isArray(el) ? el[0] : el;
                    textarea.focus();
                }
            });
        },
        cancelReply() {
            this.replyingTo = null;
            this.replyText = '';
        },
        async postReply(parentComment) {
            var videoId = this.videoId;
            var userId = this.$parent?.currentUser?.model?.id || this.userId;
            if (!videoId || !userId || !this.replyText.trim()) return;

            const commentInput = {
                videoId: videoId.trim(),
                ownerId: userId,
                message: this.replyText.trim(),
                replyToId: parentComment.id,
                commentModelRepliesId: parentComment.id,
            };

            this.loading = true;
            try {
                const commentRes = await API.graphql({
                    query: createCommentModel,
                    variables: { input: commentInput },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                const newReply = commentRes.data.createCommentModel;
                // Ensure replies array exists on parent
                if (!parentComment.replies) {
                    parentComment.replies = { items: [] };
                }
                if (!parentComment.replies.items) {
                    parentComment.replies.items = [];
                }
                parentComment.replies.items.push(newReply);
                // Also push to main comments array so it's tracked
                this.videoModel.comments.items.push(newReply);
                this.videoModel.commentsCount = (this.videoModel.commentsCount || 0) + 1;
                analyticsTrack('comment_reply', { video_id: videoId, parent_comment_id: parentComment.id });
                // Auto-expand replies for this comment
                this.expandedReplies = { ...this.expandedReplies, [parentComment.id]: true };
                this.replyingTo = null;
                this.replyText = '';
            } catch (error) {
                console.error("Error posting reply:", error);
                this.$toast.error('Failed to post reply. Please try again.');
            }
            this.loading = false;
        },
        async likeComment(comment) {
            if (!this.userId) return;
            const interaction = this.commentInteractions[comment.id];

            // If already liked, unlike instead
            if (interaction?.liked) {
                return this.unlikeComment(comment);
            }

            analyticsTrack('comment_like', { video_id: this.videoId, comment_id: comment.id });

            // Optimistic UI
            comment.likesCount = (comment.likesCount || 0) + 1;
            if (!this.commentInteractions[comment.id]) {
                this.commentInteractions[comment.id] = { liked: false, disliked: false, likeId: null, dislikeId: null };
            }
            this.commentInteractions[comment.id].liked = true;

            // If currently disliked, remove the dislike first
            if (interaction?.disliked) {
                comment.dislikesCount = Math.max(0, (comment.dislikesCount || 0) - 1);
                this.commentInteractions[comment.id].disliked = false;
                const dislikeId = interaction.dislikeId;
                this.commentInteractions[comment.id].dislikeId = null;
                if (dislikeId) {
                    try {
                        await API.graphql({
                            query: deleteCommentInteractionSimple,
                            variables: { input: { id: dislikeId } },
                            authMode: 'AMAZON_COGNITO_USER_POOLS'
                        });
                    } catch (err) {
                        console.error('Failed to remove dislike:', err);
                    }
                }
            }

            try {
                const res = await API.graphql({
                    query: createCommentInteractionSimple,
                    variables: {
                        input: {
                            type: 'like',
                            userId: this.userId,
                            commentId: comment.id,
                            videoId: this.videoId.trim()
                        }
                    },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                this.commentInteractions[comment.id].likeId = res.data.createCommentInteractionModel.id;
            } catch (error) {
                // Revert optimistic update
                comment.likesCount = Math.max(0, (comment.likesCount || 0) - 1);
                this.commentInteractions[comment.id].liked = false;
                console.error("Error liking comment:", error);
                this.$toast.error('Failed to like comment.');
            }
        },
        async unlikeComment(comment) {
            if (!this.userId) return;
            const interaction = this.commentInteractions[comment.id];
            if (!interaction?.liked || !interaction.likeId) return;

            analyticsTrack('comment_unlike', { video_id: this.videoId, comment_id: comment.id });

            // Optimistic UI
            comment.likesCount = Math.max(0, (comment.likesCount || 0) - 1);
            this.commentInteractions[comment.id].liked = false;
            const likeId = interaction.likeId;
            this.commentInteractions[comment.id].likeId = null;

            try {
                await API.graphql({
                    query: deleteCommentInteractionSimple,
                    variables: { input: { id: likeId } },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
            } catch (error) {
                // Revert
                comment.likesCount = (comment.likesCount || 0) + 1;
                this.commentInteractions[comment.id].liked = true;
                this.commentInteractions[comment.id].likeId = likeId;
                console.error("Error unliking comment:", error);
                this.$toast.error('Failed to unlike comment.');
            }
        },
        async dislikeComment(comment) {
            if (!this.userId) return;
            const interaction = this.commentInteractions[comment.id];

            // If already disliked, undislike instead
            if (interaction?.disliked) {
                return this.unDislikeComment(comment);
            }

            analyticsTrack('comment_dislike', { video_id: this.videoId, comment_id: comment.id });

            // Optimistic UI
            comment.dislikesCount = (comment.dislikesCount || 0) + 1;
            if (!this.commentInteractions[comment.id]) {
                this.commentInteractions[comment.id] = { liked: false, disliked: false, likeId: null, dislikeId: null };
            }
            this.commentInteractions[comment.id].disliked = true;

            // If currently liked, remove the like first
            if (interaction?.liked) {
                comment.likesCount = Math.max(0, (comment.likesCount || 0) - 1);
                this.commentInteractions[comment.id].liked = false;
                const likeId = interaction.likeId;
                this.commentInteractions[comment.id].likeId = null;
                if (likeId) {
                    try {
                        await API.graphql({
                            query: deleteCommentInteractionSimple,
                            variables: { input: { id: likeId } },
                            authMode: 'AMAZON_COGNITO_USER_POOLS'
                        });
                    } catch (err) {
                        console.error('Failed to remove like:', err);
                    }
                }
            }

            try {
                const res = await API.graphql({
                    query: createCommentInteractionSimple,
                    variables: {
                        input: {
                            type: 'dislike',
                            userId: this.userId,
                            commentId: comment.id,
                            videoId: this.videoId.trim()
                        }
                    },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                this.commentInteractions[comment.id].dislikeId = res.data.createCommentInteractionModel.id;
            } catch (error) {
                // Revert optimistic update
                comment.dislikesCount = Math.max(0, (comment.dislikesCount || 0) - 1);
                this.commentInteractions[comment.id].disliked = false;
                console.error("Error disliking comment:", error);
                this.$toast.error('Failed to dislike comment.');
            }
        },
        async unDislikeComment(comment) {
            if (!this.userId) return;
            const interaction = this.commentInteractions[comment.id];
            if (!interaction?.disliked || !interaction.dislikeId) return;

            analyticsTrack('comment_undislike', { video_id: this.videoId, comment_id: comment.id });

            // Optimistic UI
            comment.dislikesCount = Math.max(0, (comment.dislikesCount || 0) - 1);
            this.commentInteractions[comment.id].disliked = false;
            const dislikeId = interaction.dislikeId;
            this.commentInteractions[comment.id].dislikeId = null;

            try {
                await API.graphql({
                    query: deleteCommentInteractionSimple,
                    variables: { input: { id: dislikeId } },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
            } catch (error) {
                // Revert
                comment.dislikesCount = (comment.dislikesCount || 0) + 1;
                this.commentInteractions[comment.id].disliked = true;
                this.commentInteractions[comment.id].dislikeId = dislikeId;
                console.error("Error undisliking comment:", error);
                this.$toast.error('Failed to undislike comment.');
            }
        },
        toggleReplies(commentId) {
            this.expandedReplies = { ...this.expandedReplies, [commentId]: !this.expandedReplies[commentId] };
        },
        repliesCount(comment) {
            return comment.replies?.items?.length || 0;
        },
        async deleteReply(reply, parentComment) {
            this.loading = true;
            try {
                await API.graphql({
                    query: deleteCommentModel,
                    variables: { input: { id: reply.id } },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                // Remove from parent's replies
                if (parentComment.replies?.items) {
                    parentComment.replies.items = parentComment.replies.items.filter(r => r.id !== reply.id);
                }
                // Remove from main comments array
                this.videoModel.comments.items = this.videoModel.comments.items.filter(c => c.id !== reply.id);
                this.videoModel.commentsCount = Math.max(0, (this.videoModel.commentsCount || 0) - 1);
            } catch (error) {
                console.error("Error deleting reply:", error);
                this.$toast.error('Failed to delete reply.');
            }
            this.loading = false;
        },
        async shareVideo() {
            const title = this.videoModel.titleEN || this.videoModel.titleTA || 'Video';
            const url = window.location.href;
            if (navigator.share) {
                try {
                    await navigator.share({ title, url });
                    analyticsTrack('video_share', { video_id: this.videoId, method: 'native' });
                } catch (e) {
                    // User cancelled share or error
                    console.log('Share cancelled or failed:', e);
                }
            } else {
                try {
                    await navigator.clipboard.writeText(url);
                    analyticsTrack('video_share', { video_id: this.videoId, method: 'clipboard' });
                    this.shareCopied = true;
                    setTimeout(() => { this.shareCopied = false; }, 2000);
                } catch (e) {
                    console.error('Failed to copy URL:', e);
                }
            }
        },
        trackDownload() {
            analyticsTrack('video_download', { video_id: this.videoId });
        },
        onVideoEnded() {
            analyticsTrack('video_ended', {
                video_id: this.videoId,
                watch_percentage: this.currentTimePercentage,
                duration: this.videoDuration,
            });
            if (this.autoplayEnabled && this.nextVideo) {
                this.showAutoplayOverlay = true;
                this.autoplayCountdown = 5;
                this.startAutoplayCountdown();
            }
        },
        startAutoplayCountdown() {
            this.autoplayTimer = setInterval(() => {
                this.autoplayCountdown--;
                if (this.autoplayCountdown <= 0) {
                    this.playNextNow();
                }
            }, 1000);
        },
        cancelAutoplay() {
            if (this.autoplayTimer) {
                clearInterval(this.autoplayTimer);
                this.autoplayTimer = null;
            }
            this.showAutoplayOverlay = false;
        },
        playNextNow() {
            if (this.autoplayTimer) {
                clearInterval(this.autoplayTimer);
                this.autoplayTimer = null;
            }
            this.showAutoplayOverlay = false;
            if (this.nextVideo) {
                this.$router.push(`/watch/${this.nextVideo.id}`);
            }
        },
        toggleAutoplay() {
            this.autoplayEnabled = !this.autoplayEnabled;
            localStorage.setItem('autoplayEnabled', this.autoplayEnabled);
            analyticsTrack('autoplay_toggle', { enabled: this.autoplayEnabled });
        },
        shuffleArray(array) {
            // Fisher-Yates shuffle algorithm for unbiased randomization
            const shuffled = [...array];
            for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            return shuffled;
        }


    }
}
</script>

<style scoped>
.video-info {
    text-align: left;
}

.video-stats {
    padding: 10px 2px;
}

:deep(.vjs-big-play-button) {
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    margin: 0 !important;
    margin-top: 0 !important;
    margin-left: 0 !important;
    width: 70px !important;
    height: 70px !important;
    border-radius: 50% !important;
    background-color: rgba(0, 0, 0, 0.6) !important;
    border: 2px solid white !important;
    line-height: 70px !important;
}

:deep(.vjs-big-play-button:hover) {
    background-color: var(--accent-primary) !important;
    border-color: var(--accent-primary) !important;
}

.owner {
    margin: 10px 0px;
    font-weight: 500;
}

.video-title {
    font-size: 21px;
    font-weight: bold;
}

.comment {
    padding-top: 10px
}

.comment .user-image img {
    height: 50px;
    width: 50px;
    border-radius: 25px;
}

.comment .user-image {
    padding: 0 15px;
}

.new-comment textarea {
    border: 0;
    border-bottom: 1px solid var(--border-color);
    outline: 0;
    margin-right: 20px;
    font-size: 15px;
    background-color: transparent;
    color: var(--text-primary);
}

.video-list-item .video-thumbnail {
    height: 96px;
    width: 170px;
    margin-right: 10px;
    border-radius: 6px;
}

.video-list-item .duration {
    margin-left: 122px;
    margin-top: 70px;
}

.video-list-item {
    margin: 10px 0;
}
.related-videos {
    margin-left: 20px;
}

.video-list-item:first-child {
    margin-top: 0px;
}

.video-detail {
    margin-top: 20px;
}

button {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: #ff1314;
    color: white;
}

button:hover:not(:disabled) {
    background-color: #45a049;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.7;
}

.video-likes button,
.video-dislikes button {
    margin-left: 8px;
}

.video-likes button {
    background-color: #ff1314;
}

.video-dislikes button {
    background-color: #f44336;
}

.video-likes button:hover:not(:disabled) {
    background-color: #45a049;
}

.video-dislikes button:hover:not(:disabled) {
    background-color: #d32f2f;
}

.new-comment button {
    background-color: #ff1314;
    padding: 8px 24px;
    margin-left: 12px;
}

.new-comment button:hover:not(:disabled) {
    background-color: #45a049;
}

.comment-content button {
    background-color: #f44336;
    margin-left: 8px;
    padding: 4px 8px;
    font-size: 12px;
}

.comment-content button:hover:not(:disabled) {
    background-color: #d32f2f;
}

.owner-details button {
    background-color: #ff1314;
    margin-right: 8px;
}

.owner-details button:hover:not(:disabled) {
    background-color: #45a049;
}

.video-detail {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

/* New styles for the redesigned VideoDetail */
.video-detail-container {
    display: flex;
    gap: 20px;
    max-width: 1200px;
    margin: 20px auto;
    padding: 0 20px;
}

.video-main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.video-not-found {
    width: 100%;
    min-height: 420px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    text-align: center;
    background: var(--bg-card);
    border-radius: 8px;
    box-shadow: 0 2px 8px var(--shadow-color);
    padding: 48px 24px;
}

.video-not-found-icon {
    width: 82px;
    height: 82px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ff1314;
    background: rgba(255, 19, 20, 0.1);
}

.video-not-found h1 {
    margin: 0;
    color: var(--text-primary);
    font-size: 28px;
    line-height: 1.2;
}

.video-not-found p {
    max-width: 460px;
    margin: 0;
    color: var(--text-secondary);
    font-size: 16px;
    line-height: 1.5;
}

.video-not-found-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 8px;
}

.primary-not-found-action,
.secondary-not-found-action {
    min-height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    padding: 0 18px;
    font-weight: 600;
    text-decoration: none;
    transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.primary-not-found-action {
    gap: 8px;
    color: #fff;
    background: #ff1314;
}

.primary-not-found-action:hover {
    background: #d61011;
}

.secondary-not-found-action {
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    background: var(--bg-secondary);
}

.secondary-not-found-action:hover {
    border-color: #ff1314;
    color: #ff1314;
}

.missing-video-upload-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.missing-video-dropzone {
    min-height: 138px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 22px;
    border: 1px dashed var(--border-color);
    border-radius: 8px;
    color: var(--text-secondary);
    background: var(--bg-card);
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.missing-video-dropzone:hover {
    border-color: #ff1314;
    color: var(--text-primary);
}

.missing-video-dropzone.disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.missing-video-dropzone input {
    display: none;
}

.missing-video-dropzone span {
    max-width: 100%;
    color: var(--text-primary);
    font-size: 15px;
    font-weight: 700;
    overflow-wrap: anywhere;
}

.missing-video-dropzone small {
    color: var(--text-tertiary);
    font-size: 12px;
}

.missing-video-progress {
    display: flex;
    align-items: center;
    gap: 12px;
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 600;
}

.missing-video-progress-bar {
    flex: 1;
    height: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: var(--bg-tertiary);
}

.missing-video-progress-fill {
    height: 100%;
    border-radius: inherit;
    background: #ff1314;
    transition: width 0.2s ease;
}

.missing-video-message {
    margin: 0;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 14px;
    line-height: 1.4;
}

.missing-video-message.success {
    color: #116329;
    background: rgba(34, 197, 94, 0.12);
}

.missing-video-message.error {
    color: #b42318;
    background: rgba(255, 19, 20, 0.1);
}

.submit-missing-video {
    min-height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    gap: 8px;
    border: 0;
    border-radius: 6px;
    padding: 0 18px;
    color: #fff;
    background: #ff1314;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.2s ease, opacity 0.2s ease;
}

.submit-missing-video:hover:not(:disabled) {
    background: #d61011;
}

.submit-missing-video:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.video-player-section {
    width: 100%;
    aspect-ratio: 16 / 9;
    position: relative;
    background-color: #000;
    border-radius: 8px;
    overflow: hidden;
}

.video-player {
    width: 100%;
    height: 100%;
}

.broken-media-upload {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 14px;
    padding: clamp(16px, 3vw, 28px);
    overflow: auto;
    border-radius: inherit;
    background: #050505;
    color: #fff;
}

.broken-media-upload-copy {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    width: min(100%, 680px);
    color: #ff1314;
}

.broken-media-upload-icon {
    flex: 0 0 30px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1px;
    overflow: visible;
}

.broken-media-upload-icon .vue-feather {
    display: flex;
    overflow: visible;
}

.broken-media-upload-copy h2 {
    margin: 0 0 6px;
    color: #fff;
    font-size: 18px;
    line-height: 1.25;
}

.broken-media-upload-copy p {
    margin: 0;
    color: rgba(255, 255, 255, 0.72);
    font-size: 14px;
    line-height: 1.45;
}

.broken-media-upload .missing-video-upload-form {
    width: min(100%, 680px);
}

.broken-media-upload .missing-video-dropzone {
    min-height: 118px;
    padding: 18px;
    border-color: rgba(255, 255, 255, 0.24);
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.72);
}

.broken-media-upload .missing-video-dropzone:hover {
    border-color: #ff1314;
    color: #fff;
}

.broken-media-upload .missing-video-dropzone span {
    color: #fff;
}

.broken-media-upload .missing-video-dropzone small,
.broken-media-upload .missing-video-progress {
    color: rgba(255, 255, 255, 0.68);
}

.broken-media-upload .missing-video-progress-bar {
    background: rgba(255, 255, 255, 0.16);
}

.missing-video-url-option {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.missing-video-divider {
    display: flex;
    align-items: center;
    gap: 10px;
    color: rgba(255, 255, 255, 0.52);
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
}

.missing-video-divider::before,
.missing-video-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.16);
}

.missing-video-url-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.missing-video-url-field span {
    color: rgba(255, 255, 255, 0.72);
    font-size: 13px;
    font-weight: 700;
}

.missing-video-url-field input {
    min-height: 42px;
    border: 1px solid rgba(255, 255, 255, 0.24);
    border-radius: 6px;
    padding: 0 12px;
    color: #fff;
    background: rgba(255, 255, 255, 0.06);
    font-size: 14px;
}

.missing-video-url-field input:focus {
    outline: none;
    border-color: #ff1314;
}

.missing-video-url-field input::placeholder {
    color: rgba(255, 255, 255, 0.42);
}

.submit-missing-video.secondary {
    color: #fff;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.22);
}

.submit-missing-video.secondary:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
}

/* Video.js styles */
:deep(.video-js) {
    width: 100%;
    height: 100%;
    background-color: #000;
    position: relative !important;
}

:deep(.video-js video) {
    object-fit: contain;
}

/* Control bar alignment fix */
:deep(.video-js .vjs-control-bar) {
    display: flex;
    align-items: center;
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3em;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
}

:deep(.video-js .vjs-control) {
    display: flex;
    align-items: center;
    justify-content: center;
}

:deep(.video-js .vjs-progress-control) {
    flex: 1;
    display: flex;
    align-items: center;
}

:deep(.video-js .vjs-progress-holder) {
    flex: 1;
    height: 0.3em;
}

:deep(.video-js .vjs-time-control) {
    display: flex;
    align-items: center;
    padding: 0 0.5em;
    min-width: auto;
}

:deep(.video-js .vjs-remaining-time) {
    display: flex;
    align-items: center;
}

/* Fullscreen fixes */
:deep(.video-js.vjs-fullscreen) {
    width: 100% !important;
    height: 100% !important;
}

:deep(.video-js.vjs-fullscreen video) {
    object-fit: contain !important;
    width: 100% !important;
    height: 100% !important;
}

:deep(.video-js.vjs-fullscreen .vjs-control-bar) {
    bottom: 0;
}

.video-info-section {
    background-color: var(--bg-card);
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px var(--shadow-color);
}

.video-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
}

.video-title-section {
    flex: 1;
}

.video-title {
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 5px;
    color: var(--text-primary);
}

.video-meta {
    font-size: 14px;
    color: var(--text-tertiary);
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.video-meta .views-count,
.video-meta .upload-date,
.video-meta .video-duration,
.video-meta .significant-date {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
}

.video-meta .dot-separator {
    color: var(--text-tertiary);
    font-weight: bold;
    margin: 0 4px;
}

.video-meta .meta-icon {
    display: inline-block;
    vertical-align: middle;
    margin-right: 4px;
    color: var(--text-tertiary);
}

.video-owner-status {
    margin-top: 10px;
    display: inline-flex;
    gap: 8px;
    flex-wrap: wrap;
}

.video-status-pill {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 5px 12px;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
}

.video-status-pill.status-ready {
    background: #16a34a;
}

.video-status-pill.status-playable {
    background: #2563eb;
}

.video-status-pill.status-processing {
    background: #f59e0b;
}

.video-status-pill.status-pending {
    background: #7c3aed;
}

.video-status-pill.status-failed {
    background: #dc2626;
}

.video-status-pill.publish-published {
    background: #16a34a;
}

.video-status-pill.publish-pending {
    background: #7c3aed;
}

.video-status-pill.publish-draft {
    background: #6b7280;
}

.video-status-pill.publish-private {
    background: #0891b2;
}

.video-status-pill.publish-rejected {
    background: #dc2626;
}

.video-actions {
    display: flex;
    gap: 10px;
}

.edit-button {
    display: flex;
    align-items: center;
    gap: 5px;
    background-color: #007bff;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    transition: background-color 0.2s ease;
}

.edit-button:hover {
    background-color: #0056b3;
}

.interaction-section {
    margin-top: 15px;
    margin-bottom: 20px;
}

.interaction-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: opacity 0.3s ease;
}

.interaction-bar.interaction-loading {
    opacity: 0.4;
    pointer-events: none;
}

.interaction-left,
.interaction-center,
.interaction-right {
    display: flex;
    gap: 10px;
    align-items: center;
}

.action-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 15px;
    border-radius: 20px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid var(--border-color);
    color: var(--text-secondary);
    background-color: var(--bg-tertiary);
}

.action-button:hover:not(:disabled) {
    background-color: var(--bg-hover);
    border-color: var(--border-color);
    color: var(--text-primary);
}

.action-button.active {
    background-color: var(--bg-hover);
    border-color: var(--border-color);
    color: var(--text-primary);
}

.action-button.like-button {
    background-color: #ff1314;
    color: white;
    border-color: #ff1314;
}

.action-button.dislike-button {
    background-color: #f44336;
    color: white;
    border-color: #f44336;
}

.action-button.like-button:hover:not(:disabled) {
    background-color: #45a049;
    border-color: #45a049;
}

.action-button.dislike-button:hover:not(:disabled) {
    background-color: #d32f2f;
    border-color: #d32f2f;
}

.action-button.like-button.active {
    background-color: #ff1314;
    border-color: #ff1314;
}

.action-button.dislike-button.active {
    background-color: #f44336;
    border-color: #f44336;
}

.action-button.share-button {
    text-decoration: none;
}

.action-button.share-button span {
    transition: opacity 0.2s ease;
}

.action-button.download-button {
    text-decoration: none;
    color: var(--text-secondary);
}

.action-button.download-button:hover {
    color: var(--text-primary);
}

/* Auto-play overlay */
.autoplay-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: 8px;
    gap: 16px;
}

.autoplay-info {
    display: flex;
    align-items: center;
    gap: 14px;
    max-width: 400px;
}

.autoplay-thumbnail {
    width: 120px;
    height: 68px;
    background-size: cover;
    background-position: center;
    border-radius: 6px;
    flex-shrink: 0;
}

.autoplay-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.autoplay-label {
    font-size: 12px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.autoplay-title {
    font-size: 15px;
    font-weight: 600;
    color: white;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.autoplay-countdown {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
}

.autoplay-progress-bar {
    width: 200px;
    height: 4px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
    overflow: hidden;
}

.autoplay-progress-fill {
    height: 100%;
    background: #ff1314;
    border-radius: 2px;
    transition: width 1s linear;
}

.autoplay-actions {
    display: flex;
    gap: 12px;
}

.autoplay-play-now {
    padding: 8px 20px;
    background: #ff1314;
    color: white;
    border: none;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;
}

.autoplay-play-now:hover {
    background: #e01112;
}

.autoplay-cancel {
    padding: 8px 20px;
    background: rgba(255, 255, 255, 0.15);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;
}

.autoplay-cancel:hover {
    background: rgba(255, 255, 255, 0.25);
}

/* Sidebar header with autoplay toggle */
.sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border-color);
}

.sidebar-header .sidebar-title {
    flex: 1 1 180px;
    min-width: 0;
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
    overflow-wrap: anywhere;
}

.autoplay-toggle {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 8px;
}

.autoplay-toggle-label {
    font-size: 13px;
    color: var(--text-tertiary);
    font-weight: 500;
}

.toggle-switch {
    position: relative;
    display: inline-block;
    flex: 0 0 36px;
    width: 36px;
    height: 20px;
}

.toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-slider {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background-color: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 20px;
    transition: 0.3s;
}

.toggle-slider:before {
    content: "";
    position: absolute;
    height: 14px;
    width: 14px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    border-radius: 50%;
    transition: 0.3s;
}

.toggle-switch input:checked + .toggle-slider {
    background-color: #ff1314;
    border-color: #ff1314;
}

.toggle-switch input:checked + .toggle-slider:before {
    transform: translateX(16px);
}

/* Up Next badge */
.up-next-badge {
    position: absolute;
    top: 4px;
    left: 4px;
    background: #ff1314;
    color: white;
    font-size: 9px;
    font-weight: 700;
    padding: 2px 5px;
    border-radius: 3px;
    letter-spacing: 0.5px;
}

.channel-section {
    background-color: var(--bg-card);
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px var(--shadow-color);
    margin-top: 20px;
}

.channel-info {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
}

.channel-avatar {
    width: 50px;
    height: 50px;
    border-radius: 25px;
    overflow: hidden;
    margin-right: 15px;
}

.channel-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.channel-details {
    flex: 1;
}

.channel-name-section {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
}

.channel-name {
    font-size: 18px;
    font-weight: bold;
    color: var(--text-primary);
    text-decoration: none;
}

.channel-name:hover {
    text-decoration: underline;
}

.verified-badge {
    color: #ff1314;
    margin-left: 5px;
}

.channel-stats {
    font-size: 14px;
    color: var(--text-tertiary);
}

.channel-actions {
    display: flex;
    gap: 10px;
    margin-top: 15px;
    flex-wrap: wrap;
    align-items: center;
}

.subscribe-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 15px;
    border-radius: 20px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid #ff1314;
    color: white;
}

.subscribe-button:hover:not(:disabled) {
    background-color: #ff1314;
    color: white;
}

.subscribe-button.subscribed {
    background-color: #ff1314;
    color: white;
    border-color: #ff1314;
}

.subscribe-button.subscribed:hover:not(:disabled) {
    background-color: #45a049;
    border-color: #45a049;
}

.notification-button {
    background-color: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 20px;
    padding: 10px 15px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: var(--text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    height: 44px;
}

.notification-button:hover:not(:disabled) {
    background-color: var(--bg-hover);
    border-color: var(--border-color);
    color: var(--text-primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px var(--shadow-color);
}

.notification-button.notifications-enabled {
    background-color: #ff1314;
    border-color: #ff1314;
    color: white;
}

.notification-button.notifications-enabled:hover:not(:disabled) {
    background-color: #45a049;
    border-color: #45a049;
    color: white;
}

.notification-button svg {
    display: block;
    width: 18px;
    height: 18px;
}

.notification-button:disabled {
    background-color: #f5f5f5 !important;
    border-color: #e0e0e0 !important;
    color: #ccc !important;
    cursor: not-allowed;
    opacity: 0.6;
    transform: none !important;
}

.notification-button:disabled:hover {
    background-color: #f5f5f5 !important;
    border-color: #e0e0e0 !important;
    color: #ccc !important;
    transform: none !important;
    box-shadow: none !important;
}

.message-button {
    background-color: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 20px;
    padding: 10px 15px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: var(--text-secondary) !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    height: 44px;
    text-decoration: none !important;
}

.message-button:hover {
    background-color: var(--bg-hover);
    border-color: var(--border-color);
    color: var(--text-primary) !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px var(--shadow-color);
    text-decoration: none !important;
}

.message-button svg {
    display: block;
    width: 18px;
    height: 18px;
}

/* Ensure all vue-feather icons display properly */
.vue-feather {
    display: inline-block;
    vertical-align: middle;
}

.action-button svg,
.subscribe-button svg,
.edit-button svg,
.post-comment-button svg,
.delete-comment-button svg {
    display: inline-block;
    vertical-align: middle;
}

.video-description {
    font-size: 16px;
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 20px;
}

.video-description p {
    margin: 0 0 10px 0;
    transition: max-height 0.3s ease;
}

.description-collapsed {
    max-height: 4.8em; /* 3 lines of text (1.6 * 3) */
    overflow: hidden;
    position: relative;
}

.description-collapsed::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 1.6em;
    /* background: linear-gradient(transparent, #f9f9f9); */
    pointer-events: none;
}

.show-more-button {
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    color: #ff1314;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    padding: 8px 16px;
    margin-bottom: 10px;
    transition: color 0.2s ease;
}

.show-more-button:hover {
    color: white;
}

.show-more-button svg {
    transition: transform 0.2s ease;
}

.significant-date-section {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid var(--border-color);
}

.significant-date-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
}

.significant-date-title svg {
    color: #ff1314;
}

.significant-date-value {
    font-size: 15px;
    color: var(--text-tertiary);
    margin: 0;
    font-weight: 500;
}

.video-tags {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid var(--border-color);
}

.tags-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 10px;
}

.tags-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.tag-link {
    display: inline-block;
    padding: 6px 12px;
    background-color: var(--bg-tertiary);
    color: var(--accent-primary);
    text-decoration: none;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s ease;
    border: 1px solid var(--border-color);
}

.tag-link:hover {
    background-color: var(--accent-primary);
    color: white;
    border-color: var(--accent-primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px var(--shadow-color);
}

.video-categories {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid var(--border-color);
}

.categories-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 10px;
}

.categories-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.category-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background-color: var(--bg-tertiary);
    color: var(--text-secondary);
    text-decoration: none;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s ease;
    border: 1px solid var(--border-color);
    position: relative;
}

.category-link:hover {
    background-color: var(--bg-hover);
    color: var(--text-primary);
    border-color: var(--border-color);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px var(--shadow-color);
    text-decoration: none;
}

.category-link.primary-category {
    background-color: #d4edda;
    color: #155724;
    border-color: #c3e6cb;
}

.category-link.primary-category:hover {
    background-color: #c3e6cb;
    color: #155724;
    border-color: #b1dfbb;
}

.category-link.secondary-category {
    background-color: #fff3cd;
    color: #856404;
    border-color: #ffeaa7;
}

.category-link.secondary-category:hover {
    background-color: #ffeaa7;
    color: #856404;
    border-color: #fdcb6e;
}

.category-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 10px;
    background-color: rgba(0,0,0,0.1);
    color: inherit;
    margin-left: 4px;
}

.category-link.primary-category .category-badge {
    background-color: rgba(21, 87, 36, 0.2);
}

.category-link.secondary-category .category-badge {
    background-color: rgba(133, 100, 4, 0.2);
}

.comments-section {
    background-color: var(--bg-card);
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px var(--shadow-color);
    margin-top: 20px;
}

.comments-header {
    margin-bottom: 15px;
}

.comments-title {
    font-size: 20px;
    font-weight: bold;
    color: var(--text-primary);
    margin-bottom: 10px;
}

.comment-form {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
}

.comment-input-container {
    display: flex;
    align-items: center;
    flex: 1;
    background-color: var(--bg-primary);
    border: 1px solid var(--border-color);
    border-radius: 25px;
    padding: 5px 10px;
    box-shadow: inset 0 1px 2px var(--shadow-color);
}

.user-avatar-small {
    width: 30px;
    height: 30px;
    border-radius: 15px;
    overflow: hidden;
    margin-right: 10px;
}

.user-avatar-small img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.comment-input-wrapper {
    flex: 1;
}

.comment-input {
    width: 100%;
    border: none;
    outline: none;
    font-size: 15px;
    padding: 8px 0;
    resize: none;
    min-height: 40px;
    max-height: 100px;
    overflow-y: auto;
    background-color: transparent;
    color: var(--text-primary);
}

.comment-input::placeholder {
    color: var(--text-tertiary);
}

.comment-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
}

.post-comment-button {
    background-color: #ff1314;
    color: white;
    padding: 10px 20px;
    border-radius: 20px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border: none;
}

.post-comment-button:hover:not(:disabled) {
    background-color: #45a049;
}

.post-comment-button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.7;
}

.comments-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.comment-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 0;
    border-bottom: 1px solid var(--border-color);
}

.comment-item:last-child {
    border-bottom: none;
}

.comment-avatar {
    width: 40px;
    height: 40px;
    border-radius: 20px;
    overflow: hidden;
}

.comment-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.comment-content {
    flex: 1;
}

.comment-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 5px;
}

.comment-author {
    font-size: 15px;
    font-weight: bold;
    color: var(--text-primary);
    text-decoration: none;
}

.comment-author:hover {
    text-decoration: underline;
}

.comment-timestamp {
    font-size: 13px;
    color: var(--text-tertiary);
}

.delete-comment-button {
    background-color: #f44336;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border: none;
}

.delete-comment-button:hover:not(:disabled) {
    background-color: #d32f2f;
}

.delete-comment-button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.7;
}

.comment-text {
    font-size: 15px;
    color: var(--text-secondary);
    line-height: 1.5;
    word-break: break-word;
}

.comment-text :deep(.user-content-link) {
    color: var(--accent-primary);
    text-decoration: underline;
}

.comment-text :deep(.user-content-link:hover) {
    color: var(--accent-hover);
}

.related-videos-sidebar {
    width: 350px;
    flex-shrink: 0;
    padding: 20px;
    background-color: var(--bg-card);
    border-radius: 8px;
    box-shadow: 0 2px 8px var(--shadow-color);
}

.sidebar-title {
    font-size: 20px;
    font-weight: bold;
    color: var(--text-primary);
    margin-bottom: 15px;
    margin-top: 0px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border-color);
}

.related-videos-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.related-video-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 0;
    border-bottom: 1px solid var(--border-color);
}

.video-thumbnail-link {
    flex-shrink: 0;
    width: 100px;
    height: 70px;
    border-radius: 6px;
    overflow: hidden;
    position: relative;
}

.video-thumbnail {
    width: 100%;
    height: 100%;
    background-color: var(--bg-tertiary);
    border-radius: 6px;
    position: relative;
    overflow: hidden;
}

.video-thumbnail img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
}

.thumbnail-duration {
    position: absolute;
    bottom: 5px;
    right: 5px;
    background-color: rgba(0,0,0,0.7);
    color: white;
    padding: 3px 6px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: bold;
}

.video-info-compact {
    flex: 1;
}

.video-title-compact {
    font-size: 15px;
    font-weight: bold;
    color: var(--text-primary);
    text-decoration: none;
    display: block;
}

.video-title-compact:hover {
    text-decoration: underline;
}

.channel-name-compact {
    font-size: 12px;
    color: var(--text-tertiary);
    text-decoration: none;
    font-weight: 500;
}

.channel-name-compact:hover {
    text-decoration: underline;
}

.verified-badge-small {
    color: #ff1314;
    margin-left: 5px;
}

.video-stats-compact {
    font-size: 13px;
    color: var(--text-tertiary);
}

.no-related-videos {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 50px 0;
    color: var(--text-tertiary);
}

.no-related-videos .icon {
    margin-bottom: 15px;
}

.shimmer-sidebar-list {
    display: flex;
    flex-direction: column;
}

/* Shimmer loading skeleton */
.shimmer-container {
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

.shimmer-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
        90deg,
        transparent 0%,
        var(--bg-hover, rgba(255, 255, 255, 0.08)) 50%,
        transparent 100%
    );
    animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}

.shimmer-player {
    width: 100%;
    aspect-ratio: 16 / 9;
    background: var(--bg-tertiary);
    border-radius: 8px;
    overflow: hidden;
    position: relative;
}

.shimmer-info-section {
    background: var(--bg-card);
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px var(--shadow-color);
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.shimmer-title-long {
    height: 28px;
    width: 80%;
    background: var(--bg-tertiary);
    border-radius: 6px;
    overflow: hidden;
    position: relative;
}

.shimmer-title-medium {
    height: 20px;
    width: 50%;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
}

.shimmer-meta-row {
    display: flex;
    gap: 12px;
    margin-top: 4px;
}

.shimmer-meta-chip {
    height: 16px;
    width: 90px;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
}

.shimmer-actions-row {
    display: flex;
    gap: 15px;
    margin-top: 8px;
}

.shimmer-action-btn {
    height: 40px;
    width: 100px;
    background: var(--bg-tertiary);
    border-radius: 20px;
    overflow: hidden;
    position: relative;
}

.shimmer-channel-section {
    background: var(--bg-card);
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px var(--shadow-color);
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.shimmer-channel-row {
    display: flex;
    align-items: center;
    gap: 15px;
}

.shimmer-channel-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: var(--bg-tertiary);
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
}

.shimmer-channel-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.shimmer-channel-name {
    height: 18px;
    width: 140px;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
}

.shimmer-channel-subs {
    height: 14px;
    width: 100px;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
}

.shimmer-subscribe-btn {
    height: 40px;
    width: 110px;
    background: var(--bg-tertiary);
    border-radius: 20px;
    overflow: hidden;
    position: relative;
}

.shimmer-desc-line {
    height: 14px;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
}

.shimmer-desc-line.full {
    width: 100%;
}

.shimmer-desc-line.partial {
    width: 65%;
}

.shimmer-comments-section {
    background: var(--bg-card);
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px var(--shadow-color);
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.shimmer-comments-header {
    height: 22px;
    width: 120px;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
}

.shimmer-comment {
    display: flex;
    gap: 12px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border-color);
}

.shimmer-comment:last-child {
    border-bottom: none;
    padding-bottom: 0;
}

.shimmer-comment-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--bg-tertiary);
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
}

.shimmer-comment-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.shimmer-comment-name {
    height: 14px;
    width: 120px;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
}

.shimmer-comment-text {
    height: 14px;
    width: 100%;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
}

.shimmer-comment-text.short {
    width: 45%;
}

.shimmer-sidebar-title {
    height: 22px;
    width: 140px;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
    margin-bottom: 15px;
}

.shimmer-sidebar-item {
    display: flex;
    gap: 10px;
    padding: 10px 0;
    border-bottom: 1px solid var(--border-color);
}

.shimmer-sidebar-item:last-child {
    border-bottom: none;
}

.shimmer-sidebar-thumb {
    width: 100px;
    height: 70px;
    background: var(--bg-tertiary);
    border-radius: 6px;
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
}

.shimmer-sidebar-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
}

.shimmer-sidebar-line {
    height: 12px;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
}

.shimmer-sidebar-line.full {
    width: 100%;
}

.shimmer-sidebar-line.medium {
    width: 70%;
}

.shimmer-sidebar-line.short {
    width: 50%;
}

.comment-login-prompt {
    text-align: center;
    padding: 15px;
    margin-bottom: 20px;
}

.login-to-comment {
    color: #ff1314;
    font-weight: 500;
    text-decoration: none;
}

.login-to-comment:hover {
    text-decoration: underline;
}

/* Comment actions (like/dislike/reply) */
.comment-actions-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
}

.comment-actions-row button {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: none;
    background-color: transparent;
    border: none;
    color: var(--text-tertiary);
    font-size: 13px;
    padding: 4px 8px;
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.comment-actions-row button:hover:not(:disabled) {
    background-color: var(--bg-hover);
    color: var(--text-primary);
    transform: none;
    box-shadow: none;
}

.comment-actions-row button:disabled {
    opacity: 0.5;
    cursor: default;
    background: none;
    background-color: transparent;
}

.comment-actions-row button.reply-btn {
    color: var(--text-secondary);
    font-weight: 500;
}

.comment-actions-row button.reply-btn:hover:not(:disabled) {
    color: var(--accent-primary, #ff1314);
}

/* Reply form */
.reply-form {
    margin-top: 10px;
    padding: 10px;
    background-color: var(--bg-primary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
}

.reply-input {
    width: 100%;
    border: none;
    outline: none;
    font-size: 14px;
    padding: 6px 0;
    resize: none;
    min-height: 50px;
    max-height: 80px;
    overflow-y: auto;
    background-color: transparent;
    color: var(--text-primary);
    font-family: inherit;
}

.reply-input::placeholder {
    color: var(--text-tertiary);
}

.reply-form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 6px;
}

.reply-cancel-btn {
    background: none;
    border: 1px solid var(--border-color);
    color: var(--text-secondary);
    padding: 6px 14px;
    border-radius: 16px;
    font-size: 13px;
    cursor: pointer;
}

.reply-cancel-btn:hover {
    background-color: var(--bg-hover);
    color: var(--text-primary);
}

.reply-post-btn {
    background-color: #ff1314;
    color: white;
    padding: 6px 14px;
    border-radius: 16px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: none;
}

.reply-post-btn:hover:not(:disabled) {
    background-color: #45a049;
}

.reply-post-btn:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.7;
}

/* Toggle replies button */
button.toggle-replies-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: none;
    background-color: transparent;
    border: none;
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 500;
    padding: 6px 10px;
    margin-top: 4px;
    cursor: pointer;
}

button.toggle-replies-btn:hover {
    color: var(--text-primary);
    background: none;
    background-color: transparent;
    transform: none;
    box-shadow: none;
}

/* Replies container */
.comment-item-wrapper {
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 10px;
}

.comment-item-wrapper:last-child {
    border-bottom: none;
}

.comment-item-wrapper .comment-item {
    border-bottom: none;
}

.replies-container {
    margin-left: 48px;
    padding-left: 16px;
    border-left: 2px solid var(--border-color);
}

.reply-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 0;
}

.reply-item:last-child {
    padding-bottom: 0;
}

.reply-avatar {
    width: 32px;
    height: 32px;
    border-radius: 16px;
    overflow: hidden;
    flex-shrink: 0;
}

.reply-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.reply-item .comment-text {
    font-size: 14px;
}

.reply-item .comment-author {
    font-size: 14px;
}

.reply-item .comment-timestamp {
    font-size: 12px;
}

.reply-item .comment-actions-row button {
    font-size: 12px;
}

/* Theater Mode */
.theater-mode {
    max-width: none;
    padding: 0 15%;
    box-sizing: border-box;
    flex-direction: column;
}

.theater-mode .video-player-section {
    border-radius: 0;
}

.theater-mode .related-videos-sidebar {
    width: 100%;
}

.theater-mode .related-videos-list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

.theater-mode-button {
    margin-left: auto;
}

@media (max-width: 1024px) {
    .theater-mode-button {
        display: none;
    }
}

/* Responsive Design */
@media (max-width: 1024px) {
    .video-detail-container {
        flex-direction: column;
        gap: 15px;
    }
    
    .related-videos-sidebar {
        width: 100%;
        order: -1;
    }
    
    .video-title {
        font-size: 24px;
    }
    
    .video-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
    
    .channel-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
    
    .channel-actions {
        width: 100%;
        justify-content: flex-start;
    }
}

@media (max-width: 768px) {
    .video-detail-container {
        padding: 0 10px;
        margin: 10px auto;
    }
    
    .video-info-section,
    .channel-section,
    .comments-section,
    .related-videos-sidebar {
        padding: 15px;
    }
    
    .video-title {
        font-size: 20px;
    }
    
    .video-meta {
        gap: 6px;
        font-size: 13px;
    }
    
    .video-meta .dot-separator {
        margin: 0 2px;
    }
    
    .video-meta .meta-icon {
        margin-right: 3px;
    }
    
    .significant-date-section {
        margin-top: 10px;
        padding-top: 10px;
    }
    
    .significant-date-title {
        font-size: 14px;
    }
    
    .significant-date-value {
        font-size: 13px;
    }
    
    .video-categories {
        margin-top: 10px;
        padding-top: 10px;
    }
    
    .categories-title {
        font-size: 14px;
    }
    
    .category-link {
        font-size: 13px;
        padding: 6px 10px;
    }
    
    .video-description p {
        font-size: 15px;
    }
    
    .description-collapsed {
        max-height: 4.5em; /* Slightly less for mobile */
    }
    
    .show-more-button {
        font-size: 13px;
        padding: 6px 0;
    }
    
    .like-dislike-buttons {
        flex-direction: column;
        gap: 10px;
    }
    
    .action-button {
        width: 100%;
        justify-content: center;
    }
    
    .comment-input-container {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
    
    .comment-actions {
        width: 100%;
        justify-content: flex-end;
    }
    
    .related-video-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }

    .video-thumbnail-link {
        width: 100%;
        height: 120px;
    }

    .replies-container {
        margin-left: 28px;
        padding-left: 12px;
    }

    .broken-media-upload {
        padding: 16px;
    }

    .submit-missing-video {
        width: 100%;
    }
}

@media (max-width: 480px) {
    .video-meta {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }
    
    .video-meta .dot-separator {
        display: none;
    }
}

</style>
